<template>
    <div class="hud-wrap">
    <v-container fluid class="pa-2">

        <!-- ── Mode toggle ─────────────────────────────────────────── -->
        <v-row dense class="mb-3">
            <v-col class="d-flex align-center" style="gap: 8px;">
                <v-btn-toggle v-model="mode" mandatory density="compact" variant="outlined">
                    <v-btn value="multi-fight"  size="small" prepend-icon="mdi-account-clock">多場 × 單人</v-btn>
                    <v-btn value="multi-player" size="small" prepend-icon="mdi-account-multiple">單場 × 多人</v-btn>
                </v-btn-toggle>
                <v-spacer />
                <PlayerRenameDialog :players="renamePlayers" />
            </v-col>
        </v-row>

        <!-- ══════════════════════════════════════════════════════════ -->
        <!-- Mode A: 多場 × 單人                                       -->
        <!-- ══════════════════════════════════════════════════════════ -->
        <template v-if="mode === 'multi-fight'">

            <!-- 選擇器 -->
            <v-row dense class="mb-2">
                <v-col cols="12" sm="4">
                    <v-autocomplete
                        v-model="selectedPlayerName"
                        :items="playerOptions"
                        item-title="label"
                        item-value="name"
                        label="選擇玩家"
                        density="compact"
                        hide-details
                        clearable
                        auto-select-first
                    />
                </v-col>
                <v-col cols="12" sm="4">
                    <v-autocomplete
                        v-model="selectedRaceId"
                        :items="raceOptions"
                        item-title="label"
                        item-value="id"
                        label="選擇 Boss"
                        density="compact"
                        hide-details
                        clearable
                        auto-select-first
                        :disabled="!selectedPlayerName"
                    />
                </v-col>
                <v-col cols="12" sm="4" class="d-flex align-center flex-wrap" style="gap: 6px;">
                    <v-btn
                        v-for="q in quickBossOptions"
                        :key="q.raceId"
                        size="small"
                        :variant="selectedRaceId === q.raceId ? 'flat' : 'tonal'"
                        :color="selectedRaceId === q.raceId ? 'primary' : undefined"
                        :disabled="!selectedPlayerName || !q.available"
                        @click="selectedRaceId = selectedRaceId === q.raceId ? null : q.raceId"
                    >{{ q.name }}</v-btn>
                </v-col>
            </v-row>

            <!-- 提示 -->
            <v-row v-if="!selectedPlayerName || selectedRaceId == null" dense>
                <v-col>
                    <v-alert type="info" variant="tonal" density="compact" icon="mdi-cursor-pointer">
                        請選擇玩家與 Boss 以顯示多場比較
                    </v-alert>
                </v-col>
            </v-row>
            <v-row v-else-if="overviewItems.length === 0" dense>
                <v-col>
                    <v-alert type="warning" variant="tonal" density="compact">無符合條件的場次紀錄</v-alert>
                </v-col>
            </v-row>

            <template v-else>
                <!-- Table 1: 場次總覽 -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="overview" :class="{ 'screenshot-selected': sc.sel.has('overview') }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="collapsedModeAOverview = !collapsedModeAOverview"
                            >
                                場次總覽
                                <v-spacer />
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has('overview') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('overview')">
                                    <v-icon size="small">{{ sc.sel.has('overview') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedModeAOverview ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedModeAOverview">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <v-data-table
                                    item-value="entityKey"
                                    :headers="overviewHeaders"
                                    :items="overviewItems"
                                    density="compact"
                                    :items-per-page="-1"
                                    hide-default-footer
                                    :sort-by="[{ key: 'startAt', order: 'desc' }]"
                                >
                                    <!-- 全選 checkbox -->
                                    <template #header.checkbox>
                                        <v-checkbox-btn
                                            density="compact"
                                            :model-value="selectedFightKeys.length > 0 && selectedFightKeys.length === overviewItems.length"
                                            :indeterminate="selectedFightKeys.length > 0 && selectedFightKeys.length < overviewItems.length"
                                            @update:model-value="(v) => { const keys = overviewItems.map(i => i.entityKey); selectedFightKeys = v ? keys : []; }"
                                        />
                                    </template>
                                    <!-- 個別 checkbox -->
                                    <template #item.checkbox="{ item }">
                                        <v-checkbox-btn
                                            density="compact"
                                            :model-value="selectedFightKeys.includes(item.entityKey)"
                                            @update:model-value="(v) => { selectedFightKeys = v ? [...selectedFightKeys, item.entityKey] : selectedFightKeys.filter(k => k !== item.entityKey); }"
                                        />
                                    </template>
                                    <template #item.startAt="{ item }">
                                        <span class="text-caption">{{ fmtDateTime(item.startAt) }}</span>
                                        <span class="text-caption text-disabled ml-1">～{{ fmtTime(item.endAt) }}</span>
                                    </template>
                                    <template #item.jobName="{ item }">
                                        <span v-if="item.jobName" class="text-primary">{{ item.jobName }}</span>
                                        <span v-else class="text-disabled">—</span>
                                        <div class="text-caption text-disabled">{{ raceNameMap[item.player.raceId] ?? `Race ${item.player.raceId}` }}</div>
                                    </template>
                                    <template #item.totalDamage="{ item }">
                                        {{ Math.round(item.totalDamage).toLocaleString() }}
                                    </template>
                                    <template #item.partyRatio="{ item }">
                                        {{ fmtPct(item.totalPartyDamage > 0 ? item.totalDamage / item.totalPartyDamage : 0) }}
                                    </template>
                                    <template #item.activeDamage="{ item }">
                                        <div>{{ Math.round(item.totalDamage * item.player.activeDamageRatio).toLocaleString() }}</div>
                                        <div class="text-caption text-disabled">{{ fmtPct(item.player.activeDamageRatio) }}</div>
                                    </template>
                                    <template #item.passiveDamage="{ item }">
                                        <div>{{ Math.round(item.totalDamage * item.player.passiveDamageRatio).toLocaleString() }}</div>
                                        <div class="text-caption text-disabled">{{ fmtPct(item.player.passiveDamageRatio) }}</div>
                                    </template>
                                    <template #item.critRate="{ item }">{{ fmtPct(item.player.critRate) }}</template>
                                    <template #item.totalDPS="{ item }">{{ fmtDps(item.totalDPS) }}</template>
                                    <template #item.effectiveDPS="{ item }">{{ fmtDps(item.effectiveDPS) }}</template>
                                    <template #item.individualEffectiveDPS="{ item }">
                                        <div>{{ fmtDps(item.individualEffectiveDPS) }}</div>
                                        <div class="text-caption text-disabled">
                                            {{ fmtDuration(item.player.individualEffectiveTime) }}
                                            ({{ fmtPct(item.session.effectiveDuration > 0 ? item.player.individualEffectiveTime / item.session.effectiveDuration : 0) }})
                                        </div>
                                    </template>
                                    <!-- 平均列 -->
                                    <template #body.append>
                                        <tr v-if="overviewAvg" class="overview-avg-row">
                                            <td></td>
                                            <td class="text-start ps-4">
                                                <span class="avg-label">平均</span>
                                                <span class="text-caption text-disabled ml-1">
                                                    {{ selectedFightKeys.length }}/{{ overviewItems.length }} 場
                                                </span>
                                            </td>
                                            <td></td>
                                            <td class="text-end">{{ Math.round(overviewAvg.totalDamage).toLocaleString() }}</td>
                                            <td class="text-end">{{ fmtPct(overviewAvg.partyRatio) }}</td>
                                            <td class="text-end">
                                                <div>{{ Math.round(overviewAvg.activeDamageAbs).toLocaleString() }}</div>
                                                <div class="text-caption text-disabled">{{ fmtPct(overviewAvg.activeDamageRatio) }}</div>
                                            </td>
                                            <td class="text-end">
                                                <div>{{ Math.round(overviewAvg.passiveDamageAbs).toLocaleString() }}</div>
                                                <div class="text-caption text-disabled">{{ fmtPct(overviewAvg.passiveDamageRatio) }}</div>
                                            </td>
                                            <td class="text-end">{{ fmtPct(overviewAvg.critRate) }}</td>
                                            <td class="text-end">{{ fmtDps(overviewAvg.totalDPS) }}</td>
                                            <td class="text-end">{{ fmtDps(overviewAvg.effectiveDPS) }}</td>
                                            <td class="text-end">
                                                <div>{{ fmtDps(overviewAvg.individualEffectiveDPS) }}</div>
                                                <div class="text-caption text-disabled">
                                                    {{ fmtDuration(overviewAvg.individualEffectiveTime) }}
                                                    ({{ fmtPct(overviewAvg.effectiveDuration > 0 ? overviewAvg.individualEffectiveTime / overviewAvg.effectiveDuration : 0) }})
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Table 2: 技能明細（依職業分組） -->
                <v-row v-for="(group, gi) in fightsByJob" :key="group.job" dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" :data-screenshot-section="`skillDetail-${group.job}`" :class="{ 'screenshot-selected': sc.sel.has(`skillDetail-${group.job}`) }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="toggleSkillDetailCollapsed(group.job)"
                            >
                                技能明細
                                <span class="text-primary ml-2">{{ group.job }}</span>
                                <span class="text-caption text-disabled ml-2">{{ group.fights.length }} 場</span>
                                <v-spacer />
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has(`skillDetail-${group.job}`) ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle(`skillDetail-${group.job}`)">
                                    <v-icon size="small">{{ sc.sel.has(`skillDetail-${group.job}`) ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedSkillDetail[group.job] ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedSkillDetail[group.job]">
                            <v-divider />
                            <!-- 篩選（僅第一組顯示，避免重複） -->
                            <div v-if="gi === 0" class="d-flex flex-wrap align-center px-3 py-1 skill-filter-bar">
                                <v-checkbox v-model="filterLowRatio" label="略過傷害佔比 <0.2% 的技能" density="compact" hide-details class="flex-grow-0" />
                                <v-checkbox v-model="showActive"     label="顯示主動技能" density="compact" hide-details class="flex-grow-0 ml-3" />
                                <v-checkbox v-model="showPassive"    label="顯示被動技能" density="compact" hide-details class="flex-grow-0 ml-3" />
                                <v-checkbox v-model="showCountOnly"  label="只顯示技能次數/分" density="compact" hide-details class="flex-grow-0 ml-3" />
                            </div>
                            <v-divider v-if="gi === 0" />
                            <v-card-text class="pa-0">
                                <div style="overflow-x: auto">
                                    <table class="skill-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cell-skill-name">技能</th>
                                                <th class="cell-metric-label">指標</th>
                                                <th v-for="fight in group.fights" :key="fight.entityKey" class="cell-fight-col">
                                                    {{ fmtDateTime(fight.startAt) }}
                                                </th>
                                                <th class="cell-fight-col cell-avg">平均</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="(skillId, si) in group.skillIds" :key="skillId">
                                                <tr
                                                    v-for="(metric, mi) in (group.passiveSkillIds.has(skillId) ? activeSkillMetricsPassive : activeSkillMetrics)"
                                                    :key="mi"
                                                    :class="{ 'row-skill-sep': si > 0 && mi === 0 }"
                                                >
                                                    <td v-if="mi === 0" :rowspan="(group.passiveSkillIds.has(skillId) ? activeSkillMetricsPassive : activeSkillMetrics).length" class="cell-skill-name cell-sticky">
                                                        <div class="cmp-skill-cell">
                                                            <img
                                                                :src="`https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${skillId}.png`"
                                                                class="cmp-skill-icon"
                                                                width="28" height="28"
                                                                @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                            />
                                                            {{ skillNameMap[skillId] ?? `#${skillId}` }}
                                                        </div>
                                                    </td>
                                                    <td class="cell-metric-label">{{ metric.label }}</td>
                                                    <template v-for="fight in group.fights" :key="fight.entityKey">
                                                        <template v-for="s in [fight.player.skillStats.find(x => x.skillId === skillId)]" :key="0">
                                                            <td class="cell-val">
                                                                <template v-if="metric.key === 'ratio'">{{ s ? fmtPct(fight.player.totalDamage > 0 ? s.totalDamage / fight.player.totalDamage : 0) : '—' }}</template>
                                                                <template v-else-if="metric.key === 'totalDamage'">{{ s ? Math.round(s.totalDamage).toLocaleString() : '—' }}</template>
                                                                <template v-else-if="metric.key === 'useCount'">{{ s ? s.totalHits : '—' }}</template>
                                                                <template v-else-if="metric.key === 'usesPerMinute'">{{ s ? s.usesPerMinute.toFixed(1) : '—' }}</template>
                                                                <template v-else-if="metric.key === 'damagePerUse'">{{ s ? Math.round(s.damagePerUse).toLocaleString() : '—' }}</template>
                                                                <template v-else-if="metric.key === 'critRate'">
                                                                    <span v-if="!s || s.noCritRate" class="text-disabled">—</span>
                                                                    <span v-else>{{ fmtPct(s.critRate) }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'critDmg'">
                                                                    <span v-if="!s || s.maxCritDamage == null" class="text-disabled">—</span>
                                                                    <span v-else>{{ Math.round(s.minCritDamage!).toLocaleString() }}<span class="text-disabled"> ~ </span>{{ Math.round(s.maxCritDamage).toLocaleString() }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'nonCritDmg'">
                                                                    <span v-if="!s || s.maxNonCritDamage == null" class="text-disabled">—</span>
                                                                    <span v-else>{{ Math.round(s.minNonCritDamage!).toLocaleString() }}<span class="text-disabled"> ~ </span>{{ Math.round(s.maxNonCritDamage).toLocaleString() }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'triggerSources'">
                                                                    <div v-if="s && s.triggerSources.length" class="cmp-trig-grid">
                                                                        <template v-for="src in s.triggerSources" :key="src.skillId">
                                                                            <span
                                                                                v-if="src.skillId === OTHER_TRIGGER_SOURCE_ID"
                                                                                class="cmp-trig-icon cmp-trig-other"
                                                                                title="無法判定觸發來源"
                                                                                >其他</span
                                                                            >
                                                                            <img
                                                                                v-else
                                                                                :src="`https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${src.skillId}.png`"
                                                                                width="20" height="20"
                                                                                class="cmp-trig-icon"
                                                                                :title="skillNameMap[src.skillId] ?? `#${src.skillId}`"
                                                                                @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                                            />
                                                                            <span class="text-disabled cmp-trig-count">{{ src.count }}次</span>
                                                                            <span class="text-disabled cmp-trig-pct">{{ fmtPct(s.totalDamage > 0 ? src.damage / s.totalDamage : 0) }}</span>
                                                                        </template>
                                                                    </div>
                                                                    <span v-else class="text-disabled">—</span>
                                                                </template>
                                                            </td>
                                                        </template>
                                                    </template>
                                                    <!-- 平均欄 -->
                                                    <td v-if="metric.key === 'triggerSources'" class="cell-val cell-avg text-disabled">—</td>
                                                    <template v-else v-for="avg in [calcAvg(group.fights.map(f => ({ totalDamage: f.player.totalDamage, skillStats: f.player.skillStats })), skillId, metric.key)]" :key="0">
                                                        <td class="cell-val cell-avg">
                                                            <span v-if="avg === null" class="text-disabled">—</span>
                                                            <span v-else>{{ avg }}</span>
                                                        </td>
                                                    </template>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ── Boss 的狀態覆蓋率 ─────────────────────────────── -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="bossCond" :class="{ 'screenshot-selected': sc.sel.has('bossCond') }">
                            <v-card-title class="text-subtitle-1 py-2 px-3 d-flex align-center gap-1">
                                <span
                                    style="cursor: pointer; user-select: none; flex: 1;"
                                    class="d-flex align-center"
                                    @click="collapsedModeABossCond = !collapsedModeABossCond"
                                >
                                    Boss 的狀態覆蓋率
                                    <v-chip v-if="bossCondIncludeIds.length" size="x-small" class="ml-2" color="primary" variant="tonal">
                                        已篩選 {{ bossCondIncludeIds.length }}
                                    </v-chip>
                                </span>
                                <v-btn
                                    size="x-small"
                                    :variant="bossCondFilterOpen ? 'tonal' : 'text'"
                                    :color="bossCondIncludeIds.length ? '#00d4c8' : undefined"
                                    icon="mdi-filter-cog-outline"
                                    @click.stop="bossCondFilterOpen = !bossCondFilterOpen"
                                />
                                <v-btn icon size="x-small" variant="text" :color="sc.sel.has('bossCond') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('bossCond')">
                                    <v-icon size="small">{{ sc.sel.has('bossCond') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled" style="cursor:pointer" @click="collapsedModeABossCond = !collapsedModeABossCond">
                                    {{ collapsedModeABossCond ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                                </v-icon>
                            </v-card-title>
                            <!-- Filter bar -->
                            <div v-if="bossCondFilterOpen" class="px-3 pb-2 pt-1">
                                <v-autocomplete
                                    v-model="bossCondIncludeIds"
                                    :items="modeABossCondOptions"
                                    item-title="name"
                                    item-value="ccId"
                                    label="顯示的狀態（空白 = 全部）"
                                    density="compact"
                                    hide-details
                                    multiple
                                    chips
                                    closable-chips
                                    clearable
                                />
                            </div>
                            <v-expand-transition>
                            <div v-if="!collapsedModeABossCond">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <div v-if="!modeABossCondRows.length" class="pa-3 text-center text-disabled text-body-2">無狀態紀錄</div>
                                <div v-else style="overflow-x: auto">
                                    <table class="cov-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cov-col-name">狀態</th>
                                                <th v-for="f in filteredFightSummaries" :key="f.entityKey" class="cov-col-fight">
                                                    {{ fmtDateTime(f.startAt) }}
                                                </th>
                                                <th class="cov-col-fight cov-col-avg">平均</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in filteredModeABossCondRows" :key="row.ccId">
                                                <td class="cov-name-cell">
                                                    <div class="cov-name-inner">
                                                        <img
                                                            :src="condIconUrl(region, row.ccId)"
                                                            width="20" height="20" class="cov-cond-icon"
                                                            @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                        />
                                                        {{ row.name }}
                                                    </div>
                                                </td>
                                                <td v-for="(v, vi) in row.values" :key="vi" class="cov-val-cell">
                                                    <template v-if="v">
                                                        <div class="cov-eff">{{ fmtPct(v.eff) }}</div>
                                                        <div class="cov-tot">全場 {{ fmtPct(v.tot) }}</div>
                                                    </template>
                                                    <span v-else class="text-disabled">—</span>
                                                </td>
                                                <td class="cov-val-cell cov-col-avg">{{ fmtPct(row.avgEff) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ── 玩家的狀態覆蓋率 ───────────────────────────────── -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="playerCond" :class="{ 'screenshot-selected': sc.sel.has('playerCond') }">
                            <v-card-title class="text-subtitle-1 py-2 px-3 d-flex align-center gap-1">
                                <span
                                    style="cursor: pointer; user-select: none; flex: 1;"
                                    class="d-flex align-center"
                                    @click="collapsedModeAPlayerCond = !collapsedModeAPlayerCond"
                                >
                                    玩家的狀態覆蓋率
                                    <v-chip v-if="playerCondIncludeIds.length" size="x-small" class="ml-2" color="primary" variant="tonal">
                                        已篩選 {{ playerCondIncludeIds.length }}
                                    </v-chip>
                                </span>
                                <v-btn
                                    size="x-small"
                                    :variant="playerCondFilterOpen ? 'tonal' : 'text'"
                                    :color="playerCondIncludeIds.length ? '#00d4c8' : undefined"
                                    icon="mdi-filter-cog-outline"
                                    @click.stop="playerCondFilterOpen = !playerCondFilterOpen"
                                />
                                <v-btn icon size="x-small" variant="text" :color="sc.sel.has('playerCond') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('playerCond')">
                                    <v-icon size="small">{{ sc.sel.has('playerCond') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled" style="cursor:pointer" @click="collapsedModeAPlayerCond = !collapsedModeAPlayerCond">
                                    {{ collapsedModeAPlayerCond ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
                                </v-icon>
                            </v-card-title>
                            <!-- Filter bar -->
                            <div v-if="playerCondFilterOpen" class="px-3 pb-2 pt-1">
                                <v-autocomplete
                                    v-model="playerCondIncludeIds"
                                    :items="modeAPlayerCondOptions"
                                    item-title="name"
                                    item-value="ccId"
                                    label="顯示的狀態（空白 = 全部）"
                                    density="compact"
                                    hide-details
                                    multiple
                                    chips
                                    closable-chips
                                    clearable
                                />
                            </div>
                            <v-expand-transition>
                            <div v-if="!collapsedModeAPlayerCond">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <div v-if="!modeAPlayerCondRows.length" class="pa-3 text-center text-disabled text-body-2">無狀態紀錄</div>
                                <div v-else style="overflow-x: auto">
                                    <table class="cov-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cov-col-name">狀態</th>
                                                <th v-for="f in filteredFightSummaries" :key="f.entityKey" class="cov-col-fight">
                                                    {{ fmtDateTime(f.startAt) }}
                                                </th>
                                                <th class="cov-col-fight cov-col-avg">平均</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="row in filteredModeAPlayerCondRows" :key="row.ccId">
                                                <td class="cov-name-cell">
                                                    <img
                                                        :src="condIconUrl(region, row.ccId)"
                                                        width="20" height="20" class="cov-cond-icon"
                                                        @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                    />
                                                    {{ row.name }}
                                                </td>
                                                <td v-for="(v, vi) in row.values" :key="vi" class="cov-val-cell">
                                                    <template v-if="v">
                                                        <div class="cov-eff">{{ fmtPct(v.eff) }}</div>
                                                        <div class="cov-tot">全場 {{ fmtPct(v.tot) }}</div>
                                                    </template>
                                                    <span v-else class="text-disabled">—</span>
                                                </td>
                                                <td class="cov-val-cell cov-col-avg">{{ fmtPct(row.avgEff) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ── 自訂條件分析 ────────────────────────────────────── -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="customCond" :class="{ 'screenshot-selected': sc.sel.has('customCond') }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="collapsedModeACustomCond = !collapsedModeACustomCond"
                            >
                                自訂條件分析
                                <v-spacer />
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has('customCond') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('customCond')">
                                    <v-icon size="small">{{ sc.sel.has('customCond') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedModeACustomCond ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedModeACustomCond">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <div v-if="!modeACustomCondRows.length" class="pa-3 text-center text-disabled text-body-2">尚無自訂條件，請至 Summary 管理條件</div>
                                <div v-else style="overflow-x: auto">
                                    <table class="cov-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cov-col-name">條件</th>
                                                <th class="cov-col-metric">指標</th>
                                                <th v-for="f in filteredFightSummaries" :key="f.entityKey" class="cov-col-fight">
                                                    {{ fmtDateTime(f.startAt) }}
                                                </th>
                                                <th class="cov-col-fight cov-col-avg">平均</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="row in modeACustomCondRows" :key="row.configId">
                                                <tr>
                                                    <td rowspan="3" class="cov-name-cell font-weight-medium">{{ row.displayName }}</td>
                                                    <td class="cov-metric-label">段數</td>
                                                    <td v-for="(f, fi) in row.fights" :key="fi" class="cov-val-cell">
                                                        <span v-if="f !== null">{{ f.count }}</span>
                                                        <span v-else class="text-disabled">—</span>
                                                    </td>
                                                    <td class="cov-val-cell cov-col-avg">{{ row.avgCount.toFixed(1) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="cov-metric-label">
                                                        <div>合計傷害</div>
                                                        <div class="text-caption text-disabled">個人 / 團隊</div>
                                                    </td>
                                                    <td v-for="(f, fi) in row.fights" :key="fi" class="cov-val-cell">
                                                        <template v-if="f !== null">
                                                            <div>{{ Math.round(f.playerDamage).toLocaleString() }}</div>
                                                            <div class="text-caption text-disabled">{{ Math.round(f.totalDamage).toLocaleString() }}</div>
                                                        </template>
                                                        <span v-else class="text-disabled">—</span>
                                                    </td>
                                                    <td class="cov-val-cell cov-col-avg">
                                                        <div>{{ Math.round(row.avgPlayerDamage).toLocaleString() }}</div>
                                                        <div class="text-caption text-disabled">{{ Math.round(row.avgTotalDamage).toLocaleString() }}</div>
                                                    </td>
                                                </tr>
                                                <tr class="row-skill-sep">
                                                    <td class="cov-metric-label">
                                                        <div>每段平均</div>
                                                        <div class="text-caption text-disabled">個人 / 團隊</div>
                                                    </td>
                                                    <td v-for="(f, fi) in row.fights" :key="fi" class="cov-val-cell">
                                                        <template v-if="f !== null">
                                                            <div>{{ Math.round(f.playerAvgDamage).toLocaleString() }}</div>
                                                            <div class="text-caption text-disabled">{{ Math.round(f.avgDamage).toLocaleString() }}</div>
                                                        </template>
                                                        <span v-else class="text-disabled">—</span>
                                                    </td>
                                                    <td class="cov-val-cell cov-col-avg">
                                                        <div>{{ Math.round(row.avgPlayerAvgDamage).toLocaleString() }}</div>
                                                        <div class="text-caption text-disabled">{{ Math.round(row.avgAvgDamage).toLocaleString() }}</div>
                                                    </td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- ── 玩家的 CC 分析 ──────────────────────────────────── -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="ccAnalysis" :class="{ 'screenshot-selected': sc.sel.has('ccAnalysis') }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="collapsedModeACCAnalysis = !collapsedModeACCAnalysis"
                            >
                                玩家的 CC 分析
                                <span v-if="modeACCAnalysis?.jobConfig" class="text-primary ml-2 text-caption">{{ modeACCAnalysis.jobConfig.name }}</span>
                                <v-spacer />
                                <v-btn
                                    v-if="!collapsedModeACCAnalysis"
                                    icon
                                    size="x-small"
                                    variant="text"
                                    class="mr-1"
                                    @click.stop="ccSettingsDialogOpen = true"
                                >
                                    <v-icon size="small" class="text-disabled">mdi-cog-outline</v-icon>
                                </v-btn>
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has('ccAnalysis') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('ccAnalysis')">
                                    <v-icon size="small">{{ sc.sel.has('ccAnalysis') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedModeACCAnalysis ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedModeACCAnalysis">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <div v-if="!modeACCAnalysis || !modeACCAnalysis.visibleRules.length" class="pa-3 text-center text-disabled text-body-2">
                                    {{ modeACCAnalysis && !modeACCAnalysis.jobConfig ? '無法偵測職業（未使用職業專主技能）' : '無可顯示的規則，請至 Summary CC 分析設定' }}
                                </div>
                                <div v-else style="overflow-x: auto">
                                    <table class="cov-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cov-col-name">技能</th>
                                                <th v-for="rule in modeACCAnalysis.visibleRules" :key="rule.ruleId" class="cov-col-fight">
                                                    {{ rule.label }}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="row in modeACCAnalysis.skillRows" :key="row.skillId">
                                                <!-- 技能主列（可點擊展開） -->
                                                <tr class="cc-skill-row" @click="toggleExpandedCCSkill(row.skillId)">
                                                    <td class="cov-name-cell">
                                                        <div class="cov-name-inner">
                                                            <v-icon size="12" class="text-disabled" style="flex-shrink:0">
                                                                {{ expandedCCSkillIds.has(row.skillId) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                                                            </v-icon>
                                                            <img
                                                                :src="`https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${row.skillId}.png`"
                                                                width="18" height="18"
                                                                style="image-rendering:pixelated;border-radius:2px;flex-shrink:0"
                                                                @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                            />
                                                            {{ skillNameMap[row.skillId] ?? `#${row.skillId}` }}
                                                        </div>
                                                    </td>
                                                    <td v-for="(rs, ri) in row.ruleSummary" :key="ri" class="cov-val-cell">
                                                        <template v-if="rs">
                                                            <span :style="{ color: ccPctColor(rs.avg) }" class="font-weight-medium">
                                                                {{ rs.avg.toFixed(1) }}%
                                                            </span>
                                                            <div v-if="modeACCAnalysis.fights.length > 1 && rs.min !== rs.max" class="cov-tot">
                                                                {{ rs.min.toFixed(0) }}~{{ rs.max.toFixed(0) }}%
                                                            </div>
                                                        </template>
                                                        <span v-else class="text-disabled">—</span>
                                                    </td>
                                                </tr>
                                                <!-- 展開：各場次明細 -->
                                                <template v-if="expandedCCSkillIds.has(row.skillId)">
                                                    <tr
                                                        v-for="(fight, fi) in modeACCAnalysis.fights"
                                                        :key="fight.entityKey"
                                                        class="cc-fight-subrow"
                                                    >
                                                        <td class="cov-name-cell cc-subrow-date">{{ fmtDateTime(fight.startAt) }}</td>
                                                        <td v-for="(rs, ri) in row.ruleSummary" :key="ri" class="cov-val-cell">
                                                            <template v-if="rs && rs.fightCells[fi]">
                                                                <span :style="{ color: ccPctColor(rs.fightCells[fi]!.pct) }">
                                                                    {{ rs.fightCells[fi]!.pct.toFixed(1) }}%
                                                                </span>
                                                                <span class="cov-tot ml-1">{{ rs.fightCells[fi]!.withCC }}/{{ rs.fightCells[fi]!.total }}</span>
                                                            </template>
                                                            <span v-else class="text-disabled">—</span>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </template>
                                            <!-- 合計列 -->
                                            <tr class="cc-overall-row">
                                                <td class="cov-name-cell font-weight-bold">合計</td>
                                                <td v-for="(cell, ci) in modeACCAnalysis.overallCells" :key="ci" class="cov-val-cell">
                                                    <template v-if="cell">
                                                        <span :style="{ color: ccPctColor(cell.avg) }" class="font-weight-medium">
                                                            {{ cell.avg.toFixed(1) }}%
                                                        </span>
                                                        <div v-if="modeACCAnalysis.fights.length > 1 && cell.min !== cell.max" class="cov-tot">
                                                            {{ cell.min.toFixed(0) }}~{{ cell.max.toFixed(0) }}%
                                                        </div>
                                                    </template>
                                                    <span v-else class="text-disabled">—</span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

            </template>

        </template>

        <!-- ══════════════════════════════════════════════════════════ -->
        <!-- Mode B: 單場 × 多人                                       -->
        <!-- ══════════════════════════════════════════════════════════ -->
        <template v-else>

            <!-- 選擇器 -->
            <v-row dense class="mb-2">
                <v-col cols="12" sm="5">
                    <v-autocomplete
                        v-model="selectedFightKey"
                        :items="fightOptions"
                        item-title="label"
                        item-value="key"
                        label="選擇場次"
                        density="compact"
                        hide-details
                        clearable
                        auto-select-first
                    />
                </v-col>
                <v-col cols="12" sm="3">
                    <v-autocomplete
                        v-model="selectedJob"
                        :items="jobOptions"
                        label="選擇職業"
                        density="compact"
                        hide-details
                        clearable
                        auto-select-first
                        :disabled="!selectedFightKey"
                    />
                </v-col>
                <v-col cols="12" sm="4" class="d-flex align-center flex-wrap" style="gap: 6px;">
                    <v-btn
                        v-for="q in quickBossOptions"
                        :key="q.raceId"
                        size="small"
                        :variant="quickFightRaceId === q.raceId ? 'flat' : 'tonal'"
                        :color="quickFightRaceId === q.raceId ? 'primary' : undefined"
                        :disabled="!q.available"
                        @click="quickSelectFight(q.raceId)"
                    >{{ q.name }}</v-btn>
                </v-col>
            </v-row>

            <!-- 提示 -->
            <v-row v-if="!selectedFightKey || !selectedJob" dense>
                <v-col>
                    <v-alert type="info" variant="tonal" density="compact" icon="mdi-cursor-pointer">
                        請選擇場次與職業以顯示單場比較
                    </v-alert>
                </v-col>
            </v-row>
            <v-row v-else-if="comparedPlayers.length === 0" dense>
                <v-col>
                    <v-alert type="warning" variant="tonal" density="compact">此場次中無該職業的玩家</v-alert>
                </v-col>
            </v-row>

            <template v-else>
                <!-- Table 1: 玩家總覽 -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="modeBOverview" :class="{ 'screenshot-selected': sc.sel.has('modeBOverview') }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="collapsedModeBOverview = !collapsedModeBOverview"
                            >
                                玩家總覽
                                <span class="text-primary ml-2">{{ selectedJob }}</span>
                                <v-spacer />
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has('modeBOverview') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('modeBOverview')">
                                    <v-icon size="small">{{ sc.sel.has('modeBOverview') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedModeBOverview ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedModeBOverview">
                            <v-divider />
                            <v-card-text class="pa-0">
                                <v-data-table
                                    :headers="modeBOverviewHeaders"
                                    :items="modeBOverviewItems"
                                    density="compact"
                                    :items-per-page="-1"
                                    hide-default-footer
                                    :sort-by="[{ key: 'totalDamage', order: 'desc' }]"
                                >
                                    <template #item.name="{ item }">
                                        <span class="font-weight-medium">{{ getDisplayName(item.name) }}</span>
                                        <div class="text-caption text-disabled">{{ raceNameMap[item.raceId] ?? `Race ${item.raceId}` }}</div>
                                    </template>
                                    <template #item.totalDamage="{ item }">
                                        {{ Math.round(item.totalDamage).toLocaleString() }}
                                    </template>
                                    <template #item.partyRatio="{ item }">
                                        {{ fmtPct(item.totalPartyDamage > 0 ? item.totalDamage / item.totalPartyDamage : 0) }}
                                    </template>
                                    <template #item.activeDamage="{ item }">
                                        <div>{{ Math.round(item.totalDamage * item.activeDamageRatio).toLocaleString() }}</div>
                                        <div class="text-caption text-disabled">{{ fmtPct(item.activeDamageRatio) }}</div>
                                    </template>
                                    <template #item.passiveDamage="{ item }">
                                        <div>{{ Math.round(item.totalDamage * item.passiveDamageRatio).toLocaleString() }}</div>
                                        <div class="text-caption text-disabled">{{ fmtPct(item.passiveDamageRatio) }}</div>
                                    </template>
                                    <template #item.critRate="{ item }">{{ fmtPct(item.critRate) }}</template>
                                    <template #item.totalDPS="{ item }">{{ fmtDps(item.totalDPS) }}</template>
                                    <template #item.effectiveDPS="{ item }">{{ fmtDps(item.effectiveDPS) }}</template>
                                    <template #item.individualEffectiveDPS="{ item }">
                                        <div>{{ fmtDps(item.individualEffectiveDPS) }}</div>
                                        <div class="text-caption text-disabled">
                                            {{ fmtDuration(item.individualEffectiveTime) }}
                                            ({{ fmtPct(modeBSession && modeBSession.effectiveDuration > 0 ? item.individualEffectiveTime / modeBSession.effectiveDuration : 0) }})
                                        </div>
                                    </template>
                                    <!-- 平均列 -->
                                    <template #body.append>
                                        <tr v-if="modeBOverviewAvg" class="overview-avg-row">
                                            <td class="text-start ps-4">
                                                <span class="avg-label">平均</span>
                                                <span class="text-caption text-disabled ml-1">{{ modeBOverviewItems.length }} 人</span>
                                            </td>
                                            <td class="text-end">{{ Math.round(modeBOverviewAvg.totalDamage).toLocaleString() }}</td>
                                            <td class="text-end">{{ fmtPct(modeBOverviewAvg.partyRatio) }}</td>
                                            <td class="text-end">
                                                <div>{{ Math.round(modeBOverviewAvg.activeDamageAbs).toLocaleString() }}</div>
                                                <div class="text-caption text-disabled">{{ fmtPct(modeBOverviewAvg.activeDamageRatio) }}</div>
                                            </td>
                                            <td class="text-end">
                                                <div>{{ Math.round(modeBOverviewAvg.passiveDamageAbs).toLocaleString() }}</div>
                                                <div class="text-caption text-disabled">{{ fmtPct(modeBOverviewAvg.passiveDamageRatio) }}</div>
                                            </td>
                                            <td class="text-end">{{ fmtPct(modeBOverviewAvg.critRate) }}</td>
                                            <td class="text-end">{{ fmtDps(modeBOverviewAvg.totalDPS) }}</td>
                                            <td class="text-end">{{ fmtDps(modeBOverviewAvg.effectiveDPS) }}</td>
                                            <td class="text-end">
                                                <div>{{ fmtDps(modeBOverviewAvg.individualEffectiveDPS) }}</div>
                                                <div class="text-caption text-disabled">
                                                    {{ fmtDuration(modeBOverviewAvg.individualEffectiveTime) }}
                                                    ({{ fmtPct(modeBOverviewAvg.effectiveDuration > 0 ? modeBOverviewAvg.individualEffectiveTime / modeBOverviewAvg.effectiveDuration : 0) }})
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Table 2: 技能明細 -->
                <v-row dense class="mb-2">
                    <v-col cols="12">
                        <v-card variant="outlined" data-screenshot-section="modeBSkill" :class="{ 'screenshot-selected': sc.sel.has('modeBSkill') }">
                            <v-card-title
                                class="text-subtitle-1 py-2 px-3 d-flex align-center"
                                style="cursor: pointer; user-select: none"
                                @click="collapsedModeBSkill = !collapsedModeBSkill"
                            >
                                技能明細
                                <span class="text-primary ml-2">{{ selectedJob }}</span>
                                <v-spacer />
                                <v-btn icon size="x-small" variant="text" class="ml-1" :color="sc.sel.has('modeBSkill') ? 'primary' : undefined" title="加入截圖" @click.stop="sc.toggle('modeBSkill')">
                                    <v-icon size="small">{{ sc.sel.has('modeBSkill') ? 'mdi-camera' : 'mdi-camera-outline' }}</v-icon>
                                </v-btn>
                                <v-icon size="small" class="text-disabled">{{ collapsedModeBSkill ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                            </v-card-title>
                            <v-expand-transition>
                            <div v-if="!collapsedModeBSkill">
                            <v-divider />
                            <div class="d-flex flex-wrap align-center px-3 py-1 skill-filter-bar">
                                <v-checkbox v-model="filterLowRatio" label="略過傷害佔比 <0.2% 的技能" density="compact" hide-details class="flex-grow-0" />
                                <v-checkbox v-model="showActive"     label="顯示主動技能" density="compact" hide-details class="flex-grow-0 ml-3" />
                                <v-checkbox v-model="showPassive"    label="顯示被動技能" density="compact" hide-details class="flex-grow-0 ml-3" />
                                <v-checkbox v-model="showCountOnly"  label="只顯示技能次數/分" density="compact" hide-details class="flex-grow-0 ml-3" />
                            </div>
                            <v-divider />
                            <v-card-text class="pa-0">
                                <div style="overflow-x: auto">
                                    <table class="skill-cmp-table">
                                        <thead>
                                            <tr>
                                                <th class="cell-skill-name">技能</th>
                                                <th class="cell-metric-label">指標</th>
                                                <th v-for="p in comparedPlayers" :key="p.entityId" class="cell-fight-col">
                                                    {{ getDisplayName(p.name) }}
                                                </th>
                                                <th class="cell-fight-col cell-avg">平均</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <template v-for="(skillId, si) in modeBSkillIds" :key="skillId">
                                                <tr
                                                    v-for="(metric, mi) in (modeBPassiveSkillIds.has(skillId) ? activeSkillMetricsPassive : activeSkillMetrics)"
                                                    :key="mi"
                                                    :class="{ 'row-skill-sep': si > 0 && mi === 0 }"
                                                >
                                                    <td v-if="mi === 0" :rowspan="(modeBPassiveSkillIds.has(skillId) ? activeSkillMetricsPassive : activeSkillMetrics).length" class="cell-skill-name cell-sticky">
                                                        <div class="cmp-skill-cell">
                                                            <img
                                                                :src="`https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${skillId}.png`"
                                                                class="cmp-skill-icon"
                                                                width="28" height="28"
                                                                @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                            />
                                                            {{ skillNameMap[skillId] ?? `#${skillId}` }}
                                                        </div>
                                                    </td>
                                                    <td class="cell-metric-label">{{ metric.label }}</td>
                                                    <template v-for="p in comparedPlayers" :key="p.entityId">
                                                        <template v-for="s in [p.skillStats.find(x => x.skillId === skillId)]" :key="0">
                                                            <td class="cell-val">
                                                                <template v-if="metric.key === 'ratio'">{{ s ? fmtPct(p.totalDamage > 0 ? s.totalDamage / p.totalDamage : 0) : '—' }}</template>
                                                                <template v-else-if="metric.key === 'totalDamage'">{{ s ? Math.round(s.totalDamage).toLocaleString() : '—' }}</template>
                                                                <template v-else-if="metric.key === 'useCount'">{{ s ? s.totalHits : '—' }}</template>
                                                                <template v-else-if="metric.key === 'usesPerMinute'">{{ s ? s.usesPerMinute.toFixed(1) : '—' }}</template>
                                                                <template v-else-if="metric.key === 'damagePerUse'">{{ s ? Math.round(s.damagePerUse).toLocaleString() : '—' }}</template>
                                                                <template v-else-if="metric.key === 'critRate'">
                                                                    <span v-if="!s || s.noCritRate" class="text-disabled">—</span>
                                                                    <span v-else>{{ fmtPct(s.critRate) }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'critDmg'">
                                                                    <span v-if="!s || s.maxCritDamage == null" class="text-disabled">—</span>
                                                                    <span v-else>{{ Math.round(s.minCritDamage!).toLocaleString() }}<span class="text-disabled"> ~ </span>{{ Math.round(s.maxCritDamage).toLocaleString() }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'nonCritDmg'">
                                                                    <span v-if="!s || s.maxNonCritDamage == null" class="text-disabled">—</span>
                                                                    <span v-else>{{ Math.round(s.minNonCritDamage!).toLocaleString() }}<span class="text-disabled"> ~ </span>{{ Math.round(s.maxNonCritDamage).toLocaleString() }}</span>
                                                                </template>
                                                                <template v-else-if="metric.key === 'triggerSources'">
                                                                    <div v-if="s && s.triggerSources.length" class="cmp-trig-grid">
                                                                        <template v-for="src in s.triggerSources" :key="src.skillId">
                                                                            <span
                                                                                v-if="src.skillId === OTHER_TRIGGER_SOURCE_ID"
                                                                                class="cmp-trig-icon cmp-trig-other"
                                                                                title="無法判定觸發來源"
                                                                                >其他</span
                                                                            >
                                                                            <img
                                                                                v-else
                                                                                :src="`https://cdn.jsdelivr.net/gh/eldisa/mabinogiImage@main/SkillImage/${src.skillId}.png`"
                                                                                width="20" height="20"
                                                                                class="cmp-trig-icon"
                                                                                :title="skillNameMap[src.skillId] ?? `#${src.skillId}`"
                                                                                @error="(e: Event) => ((e.target as HTMLImageElement).style.display='none')"
                                                                            />
                                                                            <span class="text-disabled cmp-trig-count">{{ src.count }}次</span>
                                                                            <span class="text-disabled cmp-trig-pct">{{ fmtPct(s.totalDamage > 0 ? src.damage / s.totalDamage : 0) }}</span>
                                                                        </template>
                                                                    </div>
                                                                    <span v-else class="text-disabled">—</span>
                                                                </template>
                                                            </td>
                                                        </template>
                                                    </template>
                                                    <!-- 平均欄 -->
                                                    <td v-if="metric.key === 'triggerSources'" class="cell-val cell-avg text-disabled">—</td>
                                                    <template v-else v-for="avg in [calcAvg(comparedPlayers.map(p => ({ totalDamage: p.totalDamage, skillStats: p.skillStats })), skillId, metric.key)]" :key="0">
                                                        <td class="cell-val cell-avg">
                                                            <span v-if="avg === null" class="text-disabled">—</span>
                                                            <span v-else>{{ avg }}</span>
                                                        </td>
                                                    </template>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                            </v-card-text>
                            </div>
                            </v-expand-transition>
                        </v-card>
                    </v-col>
                </v-row>
            </template>

        </template>

        <!-- ══════════════════════════════════════════════════════════ -->
        <!-- 人品判斷                                                   -->
        <!-- ══════════════════════════════════════════════════════════ -->
        <v-row dense class="mt-3">
            <v-col cols="12">
                <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 d-flex align-center py-2 px-4" style="gap: 12px">
                        人品判斷
                        <template v-if="mode === 'multi-fight'">
                            <v-checkbox v-model="luckHasBarrier"  label="有結界"    density="compact" hide-details color="primary" class="flex-grow-0" />
                            <v-checkbox v-model="luckHasDestrSet" label="有開破壞套" density="compact" hide-details color="primary" class="flex-grow-0" />
                        </template>
                    </v-card-title>
                    <v-divider />

                    <!-- Mode A：各場次人品（列=項目，欄=場次） -->
                    <template v-if="mode === 'multi-fight' && luckFightDataA.length">
                        <v-card-text class="pa-2" style="overflow-x: auto">
                            <table class="luck-table">
                                <thead>
                                    <tr>
                                        <th>項目</th>
                                        <th v-for="f in luckFightDataA" :key="f.fightKey">{{ fmtDateTime(f.startAt) }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, ri) in luckFightDataA[0].rows" :key="row.label">
                                        <td>
                                            {{ row.label }}
                                            <span v-if="!row.stardustData" class="text-caption text-disabled ml-1">({{ (row.p * 100).toFixed(1) }}%)</span>
                                            <v-tooltip v-else location="right" max-width="280">
                                                <template #activator="{ props: tp }">
                                                    <v-icon v-bind="tp" icon="mdi-information-outline" size="x-small" class="ml-1 text-disabled" style="vertical-align: middle" />
                                                </template>
                                                <div>PPM ＝ 觸發次數 × 60 ÷ 個人活躍時間（秒）</div>
                                                <div>理論上限 ＝ 60 ÷ 共用 CD（目前 30 PPM）</div>
                                                <div>觸發效率 ＝ 實際 PPM ÷ 理論上限</div>
                                                <div class="mt-1 text-disabled">三個星塵共用 CD，事件不獨立，不評分</div>
                                            </v-tooltip>
                                        </td>
                                        <td v-for="f in luckFightDataA" :key="f.fightKey">
                                            <template v-if="f.rows[ri].stardustData">
                                                <div class="text-caption">{{ f.rows[ri].stardustData!.totalTriggers }} 次 · {{ f.rows[ri].stardustData!.totalPPM.toFixed(1) }} PPM<span v-if="f.rows[ri].stardustData!.totalAvgIntervalSec != null"> · {{ f.rows[ri].stardustData!.totalAvgIntervalSec!.toFixed(1) }} s</span></div>
                                                <div class="text-caption text-disabled">效率 {{ (f.rows[ri].stardustData!.triggerEfficiency * 100).toFixed(1) }}%（上限 {{ f.rows[ri].stardustData!.theoreticalMaxPPM.toFixed(0) }} PPM）</div>
                                                <div v-for="slot in f.rows[ri].stardustData!.slots" :key="slot.label" class="text-caption">
                                                    {{ slot.label }}：{{ slot.triggerCount }} 次<span v-if="slot.avgIntervalSec != null"> · {{ slot.avgIntervalSec.toFixed(1) }} s</span>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div class="text-caption">理論 {{ (f.rows[ri].n * f.rows[ri].p).toFixed(1) }} / 實際 {{ f.rows[ri].actual }}</div>
                                                <v-chip :color="zColor(zScore(f.rows[ri].actual, f.rows[ri].n, f.rows[ri].p))" size="x-small" variant="tonal">
                                                    {{ zLabel(zScore(f.rows[ri].actual, f.rows[ri].n, f.rows[ri].p)) }}
                                                </v-chip>
                                            </template>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </v-card-text>
                    </template>

                    <!-- Mode B：多玩家單場 -->
                    <template v-else-if="mode === 'multi-player' && luckRowsB.length">
                        <v-card-text class="pa-2" style="overflow-x:auto">
                            <table class="luck-table">
                                <thead>
                                    <tr>
                                        <th>項目</th>
                                        <th v-for="pb in luckRowsB" :key="pb.name">
                                            {{ pb.name }}
                                            <div class="d-flex justify-end mt-1" style="gap: 4px;">
                                                <v-chip
                                                    size="x-small"
                                                    :color="modeBPlayerToggles[pb.name]?.barrier ? 'primary' : ''"
                                                    variant="tonal"
                                                    style="cursor: pointer"
                                                    @click="togglePlayerBarrier(pb.name)"
                                                >結界</v-chip>
                                                <v-chip
                                                    size="x-small"
                                                    :color="modeBPlayerToggles[pb.name]?.destrSet ? 'primary' : ''"
                                                    variant="tonal"
                                                    style="cursor: pointer"
                                                    @click="togglePlayerDestrSet(pb.name)"
                                                >破壞套</v-chip>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-if="luckRowsB.length">
                                        <tr v-for="(row, ri) in luckRowsB[0].rows" :key="row.label">
                                            <td>
                                                {{ row.label }}
                                                <span v-if="!row.stardustData" class="text-caption text-disabled ml-1">({{ (row.p * 100).toFixed(1) }}%)</span>
                                                <v-tooltip v-else location="right" max-width="280">
                                                    <template #activator="{ props: tp }">
                                                        <v-icon v-bind="tp" icon="mdi-information-outline" size="x-small" class="ml-1 text-disabled" style="vertical-align: middle" />
                                                    </template>
                                                    <div>PPM ＝ 觸發次數 × 60 ÷ 個人活躍時間（秒）</div>
                                                    <div>觸發間隔 ＝ 個人活躍時間 ÷ 觸發次數</div>
                                                    <div>理論上限 ＝ 60 ÷ 共用 CD（目前 30 PPM）</div>
                                                    <div>觸發效率 ＝ 實際 PPM ÷ 理論上限</div>
                                                    <div class="mt-1 text-disabled">三個星塵共用 CD，事件不獨立，不評分</div>
                                                </v-tooltip>
                                            </td>
                                            <td v-for="pb in luckRowsB" :key="pb.name">
                                                <template v-if="pb.rows[ri].stardustData">
                                                    <div class="text-caption">{{ pb.rows[ri].stardustData!.totalTriggers }} 次 · {{ pb.rows[ri].stardustData!.totalPPM.toFixed(1) }} PPM</div>
                                                    <div class="text-caption text-disabled">效率 {{ (pb.rows[ri].stardustData!.triggerEfficiency * 100).toFixed(1) }}%（上限 {{ pb.rows[ri].stardustData!.theoreticalMaxPPM.toFixed(0) }} PPM）</div>
                                                    <div v-for="slot in pb.rows[ri].stardustData!.slots" :key="slot.label" class="text-caption">
                                                        {{ slot.label }}：{{ slot.triggerCount }} 次<span v-if="slot.avgIntervalSec != null"> · {{ slot.avgIntervalSec.toFixed(1) }} s</span>
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div class="text-caption">理論 {{ (pb.rows[ri].n * pb.rows[ri].p).toFixed(1) }} / 實際 {{ pb.rows[ri].actual }}</div>
                                                    <v-chip :color="zColor(zScore(pb.rows[ri].actual, pb.rows[ri].n, pb.rows[ri].p))" size="x-small" variant="tonal">
                                                        {{ zLabel(zScore(pb.rows[ri].actual, pb.rows[ri].n, pb.rows[ri].p)) }}
                                                    </v-chip>
                                                </template>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </v-card-text>
                    </template>

                    <v-card-text v-else class="text-body-2 text-disabled text-center py-4">
                        尚無資料
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- ── 截圖浮動列 ──────────────────────────────────────────── -->
        <div v-if="sc.sel.size > 0" class="screenshot-bar">
            <span class="screenshot-bar-count">已選 {{ sc.sel.size }} 區塊</span>
            <v-btn size="small" color="primary" variant="flat" :loading="sc.cap" prepend-icon="mdi-camera" @click="sc.capture">
                截圖
            </v-btn>
            <v-btn size="small" variant="text" @click="sc.sel = new Set()">
                清除
            </v-btn>
        </div>

    </v-container>

    <!-- 截圖預覽 Dialog -->
    <v-dialog v-model="sc.dlg" max-width="900" scrollable>
        <v-card>
            <v-card-title class="d-flex align-center">
                截圖預覽
                <v-spacer />
                <v-btn icon size="small" variant="text" @click="sc.dlg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-3">
                <img v-if="sc.url" :src="sc.url" style="max-width:100%; display:block" />
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn color="primary" variant="flat" prepend-icon="mdi-download" @click="sc.download">
                    下載
                </v-btn>
                <v-btn variant="text" @click="sc.dlg = false">關閉</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- CC 規則設定 Dialog -->
    <JobCCConfigDialog v-model="ccSettingsDialogOpen" />
    </div>
</template>

<script lang="ts">
import { defineComponent, inject, computed, ref, reactive, watch, onMounted, onUnmounted, nextTick, type Ref } from "vue";
import { condIconUrl } from "@/effectState";
import { ActorManager, EntityActor, type EntityDamage } from "@/eventActor";
import { buildBossSummary, OTHER_TRIGGER_SOURCE_ID, type BossSummary, type PlayerSummary, type SkillStat, type LuckStats } from "@/summaryCollector";
import { getDisplayName } from "@/store";
import { humanReadableNumber } from "@/lib/util";
import PlayerRenameDialog from "@/components/subComponents/PlayerRenameDialog.vue";
import { customConditionConfigs, bossCondIncludeIds, playerCondIncludeIds, saveCondFilters, ignoreSummonsExceptPuppet, passiveRules, luckConfig } from "@/summaryConfig";
import { jobCCConfigs, type SkillCCRule, type JobCCConfig } from "@/skillCCConfig";
import JobCCConfigDialog from "@/components/subComponents/JobCCConfigDialog.vue";

const PASSIVE_SKILL_IDS_CC = new Set([58100, 58101, 58104, 58009]);

/** 計算實體在 session 期間內的 condition 覆蓋率 */
function computeEntityCondCoverage(
    history: Array<{ At: number; List: Array<{ CCId: number }> }>,
    startAt: number, endAt: number,
    totalDur: number, effectiveDur: number,
    invincIntervals: Array<{ start: number; end: number | typeof Infinity }>,
): Map<number, { eff: number; tot: number }> {
    const acc = new Map<number, { act: number; effAct: number }>();
    if (totalDur <= 0) return new Map();
    for (let i = 0; i < history.length; i++) {
        const sStart = Math.max(history[i].At, startAt);
        const sEnd   = i + 1 < history.length ? Math.min(history[i + 1].At, endAt) : endAt;
        if (sEnd <= sStart) continue;
        const dur = sEnd - sStart;
        const invOv = invincIntervals.reduce((s, iv) => {
            const a = Math.max(iv.start, sStart);
            const b = Math.min(iv.end === Infinity ? endAt : iv.end as number, sEnd);
            return s + Math.max(0, b - a);
        }, 0);
        const effDur = Math.max(0, dur - invOv);
        for (const cond of history[i].List) {
            const e = acc.get(cond.CCId) ?? { act: 0, effAct: 0 };
            e.act    += dur;
            e.effAct += effDur;
            acc.set(cond.CCId, e);
        }
    }
    return new Map([...acc.entries()].map(([id, { act, effAct }]) => [
        id,
        { eff: effectiveDur > 0 ? effAct / effectiveDur : 0, tot: act / totalDur },
    ]));
}

function checkCCRule(damage: EntityDamage, rule: SkillCCRule): boolean {
    if (!rule.selfCCIds.length && !rule.targetCCIds.length) return true;
    const selfSet = new Set(damage.Conditions.map((c) => c.CCId));
    const targSet = new Set(damage.TargetConditions.map((c) => c.CCId));
    const selfOk = rule.selfCCIds.length === 0 || (
        (rule.selfCCMode ?? "and") === "or"
            ? rule.selfCCIds.some((id) => selfSet.has(id))
            : rule.selfCCIds.every((id) => selfSet.has(id))
    );
    const targetOk = rule.targetCCIds.length === 0 || (
        (rule.targetCCMode ?? "and") === "or"
            ? rule.targetCCIds.some((id) => targSet.has(id))
            : rule.targetCCIds.every((id) => targSet.has(id))
    );
    return selfOk && targetOk;
}

function ccPctColor(pct: number | null): string {
    if (pct === null) return "";
    if (pct >= 90) return "#4CAF50";
    if (pct >= 70) return "#FF9800";
    return "#ef9a9a";
}

// ── 技能指標定義 ─────────────────────────────────────────────────────────────
const SKILL_METRICS = [
    { key: "ratio",          label: "比例"       },
    { key: "totalDamage",    label: "總傷害"     },
    { key: "useCount",       label: "次數"       },
    { key: "usesPerMinute",  label: "次/分"      },
    { key: "damagePerUse",   label: "每擊均傷"   },
    { key: "critRate",       label: "暴擊%"      },
    { key: "critDmg",        label: "暴擊傷害"   },
    { key: "nonCritDmg",     label: "無暴擊傷害" },
] as const;

/** 被動技能不會爆擊，移除爆擊欄位，改以觸發來源列取代 */
const SKILL_METRICS_PASSIVE = [
    { key: "ratio",          label: "比例"     },
    { key: "totalDamage",    label: "總傷害"   },
    { key: "useCount",       label: "次數"     },
    { key: "usesPerMinute",  label: "次/分"    },
    { key: "damagePerUse",   label: "每擊均傷" },
    { key: "nonCritDmg",     label: "傷害"     },
    { key: "triggerSources", label: "來源"     },
] as const;

// ── Types ────────────────────────────────────────────────────────────────────
type FightEntry = {
    entityKey: string;
    startAt: number;
    endAt: number;
    session: NonNullable<BossSummary>["session"];
    totalPartyDamage: number;
    player: PlayerSummary;
};

type OverviewItem = FightEntry & {
    jobName: string | null;
    totalDamage: number;
    totalDPS: number;
    effectiveDPS: number;
    individualEffectiveDPS: number;
};

type SkillSource = { totalDamage: number; skillStats: SkillStat[] };

// ─────────────────────────────────────────────────────────────────────────────
export default defineComponent({
    name: "PlayerComparison",
    components: { PlayerRenameDialog, JobCCConfigDialog },
    setup() {
        const actorManager = inject("actorManager") as Ref<ActorManager>;
        const appEvent     = inject("appEvent")     as Ref<EventTarget>;
        const skillNameMap = inject("skillNameMap") as Ref<Record<number, string>>;
        const raceNameMap  = inject("raceNameMap")  as Ref<Record<number, string>>;
        const condNameMap  = inject("condNameMap")  as Ref<Record<number, string>>;
        const region       = inject("region")       as Ref<string>;
        const showDate     = inject("showDate")     as Ref<boolean>;

        // ── Mode ────────────────────────────────────────────────────
        const mode = ref<"multi-fight" | "multi-player">("multi-fight");

        // ── 改名（PlayerRenameDialog 所需的玩家清單） ────────────────
        const renamePlayers = computed(() =>
            Object.values(actorManager.value.entityMap)
                .filter((e) => (e as EntityActor).isPC && (e as EntityActor).name)
                .map((e) => ({ entityId: e.id, name: (e as EntityActor).name }))
                .sort((a, b) => a.name.localeCompare(b.name))
        );

        // ── 技能篩選 ─────────────────────────────────────────────
        const filterLowRatio = ref(true);
        const showActive     = ref(true);
        const showPassive    = ref(true);
        const showCountOnly  = ref(false);

        const activeSkillMetrics = computed(() =>
            showCountOnly.value ? SKILL_METRICS.filter((m) => m.key === "usesPerMinute") : (SKILL_METRICS as readonly { key: string; label: string }[])
        );
        const activeSkillMetricsPassive = computed(() =>
            showCountOnly.value ? SKILL_METRICS_PASSIVE.filter((m) => m.key === "usesPerMinute") : (SKILL_METRICS_PASSIVE as readonly { key: string; label: string }[])
        );

        // ── 區塊折疊狀態 ─────────────────────────────────────────
        const collapsedModeAOverview   = ref(false);
        const collapsedSkillDetail     = ref<Record<string, boolean>>({});
        const collapsedModeBOverview   = ref(false);
        const collapsedModeBSkill      = ref(false);
        const collapsedModeABossCond   = ref(false);
        const collapsedModeAPlayerCond = ref(false);
        const collapsedModeACustomCond = ref(false);
        const collapsedModeACCAnalysis = ref(false);
        const ccSettingsDialogOpen     = ref(false);
        const expandedCCSkillIds       = reactive(new Set<number>());
        const toggleExpandedCCSkill    = (skillId: number) => {
            if (expandedCCSkillIds.has(skillId)) expandedCCSkillIds.delete(skillId);
            else expandedCCSkillIds.add(skillId);
        };

        // ── 截圖 ──────────────────────────────────────────────────────
        const screenshotSelectedSections = ref(new Set<string>());
        const screenshotCapturing        = ref(false);
        const screenshotDialog           = ref(false);
        const screenshotDataUrl          = ref<string | null>(null);
        const screenshotName             = ref("comparison");

        const toggleSectionCapture = (id: string) => {
            const s = new Set(screenshotSelectedSections.value);
            if (s.has(id)) s.delete(id); else s.add(id);
            screenshotSelectedSections.value = s;
        };

        const captureSelectedSections = async () => {
            if (!screenshotSelectedSections.value.size || screenshotCapturing.value) return;
            screenshotCapturing.value = true;
            try {
                const collapseBackup: Record<string, boolean> = {};
                for (const id of screenshotSelectedSections.value) {
                    if (id === 'overview')        { collapseBackup[id] = collapsedModeAOverview.value;   collapsedModeAOverview.value   = false; }
                    else if (id === 'bossCond')   { collapseBackup[id] = collapsedModeABossCond.value;   collapsedModeABossCond.value   = false; }
                    else if (id === 'playerCond') { collapseBackup[id] = collapsedModeAPlayerCond.value; collapsedModeAPlayerCond.value = false; }
                    else if (id === 'customCond') { collapseBackup[id] = collapsedModeACustomCond.value; collapsedModeACustomCond.value = false; }
                    else if (id === 'ccAnalysis') { collapseBackup[id] = collapsedModeACCAnalysis.value; collapsedModeACCAnalysis.value = false; }
                    else if (id === 'modeBOverview') { collapseBackup[id] = collapsedModeBOverview.value; collapsedModeBOverview.value = false; }
                    else if (id === 'modeBSkill')    { collapseBackup[id] = collapsedModeBSkill.value;    collapsedModeBSkill.value    = false; }
                    else if (id.startsWith('skillDetail-')) {
                        const job = id.slice('skillDetail-'.length);
                        collapseBackup[id] = collapsedSkillDetail.value[job] ?? false;
                        if (collapsedSkillDetail.value[job]) toggleSkillDetailCollapsed(job);
                    }
                }
                await nextTick();
                await new Promise(r => setTimeout(r, 120));

                const { default: html2canvas } = await import("html2canvas");
                const canvases: HTMLCanvasElement[] = [];
                for (const id of screenshotSelectedSections.value) {
                    const el = document.querySelector(`[data-screenshot-section="${id}"]`) as HTMLElement | null;
                    if (!el) continue;
                    // 技能明細欄位少時，縮到內容寬度避免大量空白
                    const fitWidth = id.startsWith('skillDetail-');
                    const prevWidth = fitWidth ? el.style.width : '';
                    if (fitWidth) el.style.width = 'fit-content';
                    const canvas = await html2canvas(el, { useCORS: true, scale: 2, backgroundColor: '#0a0d14' });
                    if (fitWidth) el.style.width = prevWidth;
                    canvases.push(canvas);
                }
                if (canvases.length) {
                    const totalH = canvases.reduce((s, c) => s + c.height, 0);
                    const maxW   = Math.max(...canvases.map(c => c.width));
                    const merged = document.createElement('canvas');
                    merged.width = maxW; merged.height = totalH;
                    const ctx = merged.getContext('2d')!;
                    let y = 0;
                    for (const c of canvases) { ctx.drawImage(c, 0, y); y += c.height; }
                    screenshotDataUrl.value = merged.toDataURL('image/png');
                    screenshotDialog.value  = true;
                }

                for (const id of screenshotSelectedSections.value) {
                    if (id === 'overview')        { collapsedModeAOverview.value   = collapseBackup[id]; }
                    else if (id === 'bossCond')   { collapsedModeABossCond.value   = collapseBackup[id]; }
                    else if (id === 'playerCond') { collapsedModeAPlayerCond.value = collapseBackup[id]; }
                    else if (id === 'customCond') { collapsedModeACustomCond.value = collapseBackup[id]; }
                    else if (id === 'ccAnalysis') { collapsedModeACCAnalysis.value = collapseBackup[id]; }
                    else if (id === 'modeBOverview') { collapsedModeBOverview.value = collapseBackup[id]; }
                    else if (id === 'modeBSkill')    { collapsedModeBSkill.value    = collapseBackup[id]; }
                    else if (id.startsWith('skillDetail-')) {
                        const job = id.slice('skillDetail-'.length);
                        if (collapseBackup[id] !== (collapsedSkillDetail.value[job] ?? false)) toggleSkillDetailCollapsed(job);
                    }
                }
            } finally {
                screenshotCapturing.value = false;
            }
        };

        const downloadScreenshot = () => {
            if (!screenshotDataUrl.value) return;
            const a = document.createElement('a');
            a.href     = screenshotDataUrl.value;
            a.download = `${screenshotName.value}.png`;
            a.click();
        };

        // 截圖狀態整合物件（減少 setup return 屬性數以避免 vue-tsc 型別截斷）
        const sc = reactive({
            sel: screenshotSelectedSections,
            cap: screenshotCapturing,
            dlg: screenshotDialog,
            url: screenshotDataUrl,
            toggle: toggleSectionCapture,
            capture: captureSelectedSections,
            download: downloadScreenshot,
        });

        const toggleSkillDetailCollapsed = (job: string) => {
            collapsedSkillDetail.value = {
                ...collapsedSkillDetail.value,
                [job]: !collapsedSkillDetail.value[job],
            };
        };

        // ── 共用：快速 Boss 按鈕 ────────────────────────────────────
        const QUICK_RACE_IDS = [7603, 7602, 7600, 7601, 7615] as const;

        const quickBossOptions = computed(() => {
            const map = actorManager.value.entityMap;
            const allBossRaceIds = new Set(
                Object.values(map)
                    .filter((e) => !(e as EntityActor).isPC && !(e as EntityActor).ownerId && (e as EntityActor).takeDamages.length > 0)
                    .map((e) => (e as EntityActor).raceId),
            );
            return QUICK_RACE_IDS.map((raceId) => ({
                raceId,
                name: raceNameMap.value[raceId] ?? `Race ${raceId}`,
                available: allBossRaceIds.has(raceId),
            }));
        });

        // ────────────────────────────────────────────────────────────
        // Mode A：多場 × 單人
        // ────────────────────────────────────────────────────────────
        const selectedPlayerName = ref<string>("");
        const selectedRaceId     = ref<number | null>(null);

        const onClear = () => {
            selectedPlayerName.value = "";
            selectedRaceId.value     = null;
        };
        appEvent.value.addEventListener("clear", onClear);
        onUnmounted(() => appEvent.value.removeEventListener("clear", onClear));

        // 召喚物傷害輔助：ownerId → 該召喚物的所有 applyDamages
        const summonDamagesByOwner = computed((): Map<string, EntityDamage[]> => {
            const result = new Map<string, EntityDamage[]>();
            for (const e of Object.values(actorManager.value.entityMap)) {
                const ea = e as EntityActor;
                if (!ea.ownerId || !ea.applyDamages.length) continue;
                const arr = result.get(ea.ownerId) ?? [];
                arr.push(...ea.applyDamages);
                result.set(ea.ownerId, arr);
            }
            return result;
        });

        const getAllDamages = (entityKey: string, ea: EntityActor): EntityDamage[] => {
            const own = ea.applyDamages;
            const summon = summonDamagesByOwner.value.get(entityKey);
            return summon ? [...own, ...summon] : own;
        };

        const playerOptions = computed(() => {
            const map = actorManager.value.entityMap;
            const bossKeys = new Set(
                Object.entries(map)
                    .filter(([, e]) => !(e as EntityActor).isPC && !(e as EntityActor).ownerId)
                    .map(([k]) => k),
            );
            const damageByName = new Map<string, number>();
            for (const [key, e] of Object.entries(map)) {
                const ea = e as EntityActor;
                if (!ea.isPC || !ea.name) continue;
                const total = getAllDamages(key, ea)
                    .filter((d) => bossKeys.has(d.TargetId))
                    .reduce((s, d) => s + d.Damage, 0);
                if (total > 0) damageByName.set(ea.name, (damageByName.get(ea.name) ?? 0) + total);
            }
            return [...damageByName.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([name, total]) => ({ name, label: `${getDisplayName(name)}  ${Math.round(total).toLocaleString()}` }));
        });

        const raceOptions = computed(() => {
            const map = actorManager.value.entityMap;
            const seen = new Map<number, string>();
            if (!selectedPlayerName.value) {
                for (const e of Object.values(map)) {
                    const ea = e as EntityActor;
                    if (ea.isPC || ea.ownerId || !ea.takeDamages.length) continue;
                    if (!seen.has(ea.raceId)) { const n = raceNameMap.value[ea.raceId] ?? `Race ${ea.raceId}`; seen.set(ea.raceId, `${n}${n.includes(String(ea.raceId)) ? '' : `（#${ea.raceId}）`}`); }
                }
            } else {
                const playerTargets = new Set<string>();
                for (const [key, e] of Object.entries(map)) {
                    const ea = e as EntityActor;
                    if (!ea.isPC || ea.name !== selectedPlayerName.value) continue;
                    for (const d of getAllDamages(key, ea)) playerTargets.add(d.TargetId);
                }
                for (const [key, e] of Object.entries(map)) {
                    const ea = e as EntityActor;
                    if (ea.isPC || ea.ownerId || !ea.takeDamages.length || !playerTargets.has(key)) continue;
                    if (!seen.has(ea.raceId)) { const n = raceNameMap.value[ea.raceId] ?? `Race ${ea.raceId}`; seen.set(ea.raceId, `${n}${n.includes(String(ea.raceId)) ? '' : `（#${ea.raceId}）`}`); }
                }
            }
            return [...seen.entries()].map(([id, label]) => ({ id, label }));
        });

        // ── Mode A 場次選擇 ─────────────────────────────────────────
        const selectedFightKeys = ref<string[]>([]);

        const fightSummaries = computed((): FightEntry[] => {
            if (!selectedPlayerName.value || selectedRaceId.value == null) return [];
            const results: FightEntry[] = [];
            for (const [key, e] of Object.entries(actorManager.value.entityMap)) {
                const ea = e as EntityActor;
                if (ea.isPC || ea.ownerId || ea.raceId !== selectedRaceId.value || !ea.takeDamages.length) continue;
                const summary = buildBossSummary(key, actorManager.value, [], undefined, { ignoreSummonsExceptPuppet: ignoreSummonsExceptPuppet.value, passiveRules: passiveRules, stardustConfigs: luckConfig.value.stardustConfigs });
                if (!summary) continue;
                const player = summary.players.find((p) => p.name === selectedPlayerName.value);
                if (!player) continue;
                results.push({ entityKey: key, startAt: summary.session.startAt, endAt: summary.session.endAt, session: summary.session, totalPartyDamage: summary.totalDamage, player });
            }
            return results.sort((a, b) => b.startAt - a.startAt);
        });

        // 只在玩家/BOSS 切換時重設選擇（預設全選）；fightSummaries 因 reactive 重算不觸發重設
        watch([selectedPlayerName, selectedRaceId], () => {
            selectedFightKeys.value = fightSummaries.value.map(f => f.entityKey);
        }, { immediate: true });

        // 依選擇過濾場次（供 技能明細 / CC 分析 使用）
        const filteredFightSummaries = computed(() => {
            const keys = new Set(selectedFightKeys.value);
            return fightSummaries.value.filter(f => keys.has(f.entityKey));
        });

        const overviewItems = computed((): OverviewItem[] =>
            fightSummaries.value.map((f) => ({
                ...f,
                jobName:                f.player.jobName,
                totalDamage:            f.player.totalDamage,
                totalDPS:               f.player.totalDPS,
                effectiveDPS:           f.player.effectiveDPS,
                individualEffectiveDPS: f.player.individualEffectiveDPS,
            })),
        );

        const toggleFight = (key: string, selected: boolean) => {
            if (selected) {
                if (!selectedFightKeys.value.includes(key))
                    selectedFightKeys.value = [...selectedFightKeys.value, key];
            } else {
                selectedFightKeys.value = selectedFightKeys.value.filter(k => k !== key);
            }
        };

        const toggleAllFights = (selectAll: boolean) => {
            selectedFightKeys.value = selectAll ? overviewItems.value.map(i => i.entityKey) : [];
        };

        const overviewAvg = computed(() => {
            const selectedKeys = new Set(selectedFightKeys.value);
            const items = overviewItems.value.filter(i => selectedKeys.has(i.entityKey));
            if (items.length < 2) return null;
            const mean = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / vals.length;
            return {
                totalDamage:             mean(items.map((i) => i.totalDamage)),
                partyRatio:              mean(items.map((i) => i.totalPartyDamage > 0 ? i.totalDamage / i.totalPartyDamage : 0)),
                activeDamageAbs:         mean(items.map((i) => i.totalDamage * i.player.activeDamageRatio)),
                activeDamageRatio:       mean(items.map((i) => i.player.activeDamageRatio)),
                passiveDamageAbs:        mean(items.map((i) => i.totalDamage * i.player.passiveDamageRatio)),
                passiveDamageRatio:      mean(items.map((i) => i.player.passiveDamageRatio)),
                critRate:                mean(items.map((i) => i.player.critRate)),
                totalDPS:                mean(items.map((i) => i.totalDPS)),
                effectiveDPS:            mean(items.map((i) => i.effectiveDPS)),
                individualEffectiveDPS:  mean(items.map((i) => i.individualEffectiveDPS)),
                individualEffectiveTime: mean(items.map((i) => i.player.individualEffectiveTime)),
                effectiveDuration:       mean(items.map((i) => i.session.effectiveDuration)),
            };
        });

        const fightsByJob = computed(() => {
            const groups = new Map<string, FightEntry[]>();
            for (const f of filteredFightSummaries.value) {
                const job = f.player.jobName ?? "未知職業";
                if (!groups.has(job)) groups.set(job, []);
                groups.get(job)!.push(f);
            }
            return [...groups.entries()].map(([job, fights]) => {
                const skillTotals   = new Map<number, number>();
                const skillIsPassive = new Map<number, boolean>();
                const totalPlayerDamage = fights.reduce((s, f) => s + f.player.totalDamage, 0);
                for (const f of fights) {
                    for (const s of f.player.skillStats) {
                        skillTotals.set(s.skillId, (skillTotals.get(s.skillId) ?? 0) + s.totalDamage);
                        if (!skillIsPassive.has(s.skillId))
                            skillIsPassive.set(s.skillId, s.triggerSources.length > 0);
                    }
                }
                const skillIds = [...skillTotals.entries()]
                    .filter(([id, dmg]) => {
                        if (filterLowRatio.value && totalPlayerDamage > 0 && dmg / totalPlayerDamage < 0.002) return false;
                        const isPassive = skillIsPassive.get(id) ?? false;
                        if (!showPassive.value && isPassive)  return false;
                        if (!showActive.value  && !isPassive) return false;
                        return true;
                    })
                    .sort((a, b) => b[1] - a[1])
                    .map(([id]) => id);
                const passiveSkillIds = new Set(
                    skillIds.filter((id) => skillIsPassive.get(id) ?? false)
                );
                return { job, fights, skillIds, passiveSkillIds };
            });
        });

        // ── Mode A 新增區塊：Boss 狀態覆蓋率 ─────────────────────────
        const modeABossCondRows = computed(() => {
            if (!filteredFightSummaries.value.length) return [];
            const fightMaps = filteredFightSummaries.value.map((f) => {
                const boss = actorManager.value.entityMap[f.entityKey] as EntityActor | undefined;
                if (!boss) return new Map<number, { eff: number; tot: number }>();
                return computeEntityCondCoverage(
                    boss.conditionHistory, f.session.startAt, f.session.endAt,
                    f.session.totalDuration, f.session.effectiveDuration,
                    f.session.invincibleIntervals,
                );
            });
            const allIds = new Set<number>();
            for (const m of fightMaps) for (const id of m.keys()) allIds.add(id);
            return [...allIds].map((ccId) => {
                const values = fightMaps.map((m) => m.get(ccId) ?? null);
                const effs   = values.filter((v): v is { eff: number; tot: number } => v !== null).map((v) => v.eff);
                const avgEff = effs.length ? effs.reduce((a, b) => a + b, 0) / effs.length : 0;
                return { ccId, name: condNameMap.value[ccId] ?? `#${ccId}`, values, avgEff };
            }).sort((a, b) => b.avgEff - a.avgEff);
        });

        // Boss cond filter options（給 autocomplete 用）
        const modeABossCondOptions = computed(() =>
            modeABossCondRows.value.map((r) => ({ ccId: r.ccId, name: r.name }))
        );
        const filteredModeABossCondRows = computed(() => {
            if (!bossCondIncludeIds.value.length) return modeABossCondRows.value;
            const ids = new Set(bossCondIncludeIds.value);
            return modeABossCondRows.value.filter((r) => ids.has(r.ccId));
        });
        const bossCondFilterOpen = ref(false);

        // ── Mode A 新增區塊：玩家狀態覆蓋率 ─────────────────────────
        const modeAPlayerCondRows = computed(() => {
            if (!filteredFightSummaries.value.length) return [];
            const fightMaps = filteredFightSummaries.value.map((f) => {
                const player = actorManager.value.entityMap[f.player.entityId] as EntityActor | undefined;
                if (!player) return new Map<number, { eff: number; tot: number }>();
                return computeEntityCondCoverage(
                    player.conditionHistory, f.session.startAt, f.session.endAt,
                    f.session.totalDuration, f.session.effectiveDuration,
                    f.session.invincibleIntervals,
                );
            });
            const allIds = new Set<number>();
            for (const m of fightMaps) for (const id of m.keys()) allIds.add(id);
            return [...allIds].map((ccId) => {
                const values = fightMaps.map((m) => m.get(ccId) ?? null);
                const effs   = values.filter((v): v is { eff: number; tot: number } => v !== null).map((v) => v.eff);
                const avgEff = effs.length ? effs.reduce((a, b) => a + b, 0) / effs.length : 0;
                return { ccId, name: condNameMap.value[ccId] ?? `#${ccId}`, values, avgEff };
            }).sort((a, b) => b.avgEff - a.avgEff);
        });

        // Player cond filter options（給 autocomplete 用）
        const modeAPlayerCondOptions = computed(() =>
            modeAPlayerCondRows.value.map((r) => ({ ccId: r.ccId, name: r.name }))
        );
        const filteredModeAPlayerCondRows = computed(() => {
            if (!playerCondIncludeIds.value.length) return modeAPlayerCondRows.value;
            const ids = new Set(playerCondIncludeIds.value);
            return modeAPlayerCondRows.value.filter((r) => ids.has(r.ccId));
        });
        const playerCondFilterOpen = ref(false);

        // 持久化（當 filter 在 playerComparison 頁面改動時也要存）
        watch(bossCondIncludeIds,   saveCondFilters, { deep: true });
        watch(playerCondIncludeIds, saveCondFilters, { deep: true });

        // ── Mode A 新增區塊：自訂條件分析 ────────────────────────────
        const modeACustomCondRows = computed(() => {
            if (!filteredFightSummaries.value.length || !customConditionConfigs.value.length) return [];
            const fightSummaryList = filteredFightSummaries.value.map((f) =>
                buildBossSummary(f.entityKey, actorManager.value, customConditionConfigs.value, undefined, { ignoreSummonsExceptPuppet: ignoreSummonsExceptPuppet.value, passiveRules: passiveRules, stardustConfigs: luckConfig.value.stardustConfigs }),
            );
            const playerName = selectedPlayerName.value;
            return customConditionConfigs.value.map((cfg) => {
                const fights = fightSummaryList.map((s) => {
                    if (!s) return null;
                    const r = s.customConditions.find((c) => c.configId === cfg.id);
                    if (!r) return null;
                    const playerDmg = r.groups.reduce((sum, g) => {
                        const p = g.players.find((pl) => pl.name === playerName);
                        return sum + (p ? p.totalDamage : 0);
                    }, 0);
                    const playerAvgDmg = r.groups.length > 0 ? playerDmg / r.groups.length : 0;
                    return {
                        count: r.groups.length,
                        totalDamage: r.totalDamage,
                        avgDamage: r.averageDamage,
                        playerDamage: playerDmg,
                        playerAvgDamage: playerAvgDmg,
                    };
                });
                const valid = fights.filter((f): f is NonNullable<typeof f> => f !== null);
                return {
                    configId:            cfg.id,
                    displayName:         cfg.displayName,
                    fights,
                    avgCount:            valid.length ? valid.reduce((s, f) => s + f.count, 0)              / valid.length : 0,
                    avgTotalDamage:      valid.length ? valid.reduce((s, f) => s + f.totalDamage, 0)        / valid.length : 0,
                    avgAvgDamage:        valid.length ? valid.reduce((s, f) => s + f.avgDamage, 0)          / valid.length : 0,
                    avgPlayerDamage:     valid.length ? valid.reduce((s, f) => s + f.playerDamage, 0)       / valid.length : 0,
                    avgPlayerAvgDamage:  valid.length ? valid.reduce((s, f) => s + f.playerAvgDamage, 0)    / valid.length : 0,
                };
            });
        });

        // ── Mode A 新增區塊：玩家 CC 分析（技能 × CC規則，可展開各場次） ──
        const modeACCAnalysis = computed(() => {
            if (!filteredFightSummaries.value.length) return null;
            const ccConfigs = jobCCConfigs.value;
            const fights = filteredFightSummaries.value;

            // 偵測職業
            const allUsedSkillIds = new Set<number>();
            for (const f of fights) {
                const entity = actorManager.value.entityMap[f.player.entityId] as EntityActor | undefined;
                if (!entity) continue;
                for (const d of getAllDamages(f.player.entityId, entity))
                    if (d.TargetId === f.entityKey) allUsedSkillIds.add(d.SkillId);
            }
            let jobConfig: JobCCConfig | null = null;
            for (const cfg of ccConfigs) {
                if (cfg.detectionSkillIds.some((id) => allUsedSkillIds.has(id))) { jobConfig = cfg; break; }
            }
            if (!jobConfig) {
                let best: JobCCConfig | null = null; let bestScore = 0;
                for (const cfg of ccConfigs) {
                    const score = cfg.detectionSkillIds.filter((id) => allUsedSkillIds.has(id)).length;
                    if (score > bestScore) { bestScore = score; best = cfg; }
                }
                if (bestScore > 0) jobConfig = best;
            }

            type FightCell = { withCC: number; total: number; pct: number } | null;
            type RuleSummary = { avg: number; min: number; max: number; fightCells: FightCell[] } | null;
            type SkillRow = { skillId: number; ruleSummary: RuleSummary[] };
            type OverallCell = { avg: number; min: number; max: number } | null;

            const visibleRules = (jobConfig?.rules ?? []).filter((r) => r.showInTable !== false);
            if (!visibleRules.length) return { jobConfig, visibleRules: [] as SkillCCRule[], fights, skillRows: [] as SkillRow[], overallCells: [] as OverallCell[] };

            // 各場次按技能整理命中（排除被動）
            const fightHitMaps = fights.map((f) => {
                const entity = actorManager.value.entityMap[f.player.entityId] as EntityActor | undefined;
                const map = new Map<number, EntityDamage[]>();
                if (!entity) return map;
                for (const d of getAllDamages(f.player.entityId, entity)) {
                    if (d.TargetId !== f.entityKey || d.Damage <= 0 || d.At < f.session.startAt || d.At > f.session.endAt || PASSIVE_SKILL_IDS_CC.has(d.SkillId)) continue;
                    const arr = map.get(d.SkillId) ?? [];
                    arr.push(d);
                    map.set(d.SkillId, arr);
                }
                return map;
            });

            // 技能列，依總命中次數排序
            const skillHitCounts = new Map<number, number>();
            for (const map of fightHitMaps)
                for (const [sid, hits] of map)
                    skillHitCounts.set(sid, (skillHitCounts.get(sid) ?? 0) + hits.length);

            const skillRows: SkillRow[] = [...skillHitCounts.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([skillId]) => {
                    const ruleSummary: RuleSummary[] = visibleRules.map((rule) => {
                        const fightCells: FightCell[] = fights.map((_, fi) => {
                            const skillHits = fightHitMaps[fi].get(skillId) ?? [];
                            const ruleHits = rule.skillIds.length > 0
                                ? skillHits.filter((d) => rule.skillIds.includes(d.SkillId))
                                : skillHits;
                            if (!ruleHits.length) return null;
                            const withCC = ruleHits.filter((d) => checkCCRule(d, rule)).length;
                            return { withCC, total: ruleHits.length, pct: (withCC / ruleHits.length) * 100 };
                        });
                        const valid = fightCells.filter(Boolean) as NonNullable<FightCell>[];
                        if (!valid.length) return null;
                        const pcts = valid.map((c) => c.pct);
                        return { avg: pcts.reduce((a, b) => a + b, 0) / pcts.length, min: Math.min(...pcts), max: Math.max(...pcts), fightCells };
                    });
                    return { skillId, ruleSummary };
                })
                .filter((row) => row.ruleSummary.some((r) => r !== null));

            // 合計列（各場次全部非被動命中合計）
            const overallCells: OverallCell[] = visibleRules.map((rule) => {
                const fightPcts = fights.map((_, fi) => {
                    const allHits = [...fightHitMaps[fi].values()].flat();
                    const ruleHits = rule.skillIds.length > 0 ? allHits.filter((d) => rule.skillIds.includes(d.SkillId)) : allHits;
                    if (!ruleHits.length) return null;
                    const withCC = ruleHits.filter((d) => checkCCRule(d, rule)).length;
                    return (withCC / ruleHits.length) * 100;
                }).filter(Boolean) as number[];
                if (!fightPcts.length) return null;
                return { avg: fightPcts.reduce((a, b) => a + b, 0) / fightPcts.length, min: Math.min(...fightPcts), max: Math.max(...fightPcts) };
            });

            return { jobConfig, visibleRules, fights, skillRows, overallCells };
        });

        const overviewHeaders = [
            { title: "",             key: "checkbox",               sortable: false, width: "48px"  },
            { title: "場次",         key: "startAt",               sortable: true,  align: "start" as const },
            { title: "職業",         key: "jobName",               sortable: true,  align: "start" as const },
            { title: "總傷害",       key: "totalDamage",           sortable: true,  align: "end"   as const },
            { title: "隊伍佔比",     key: "partyRatio",            sortable: false, align: "end"   as const },
            { title: "主動傷害",     key: "activeDamage",          sortable: false, align: "end"   as const },
            { title: "被動傷害",     key: "passiveDamage",         sortable: false, align: "end"   as const },
            { title: "暴擊率",       key: "critRate",              sortable: false, align: "end"   as const },
            { title: "全場 DPS",     key: "totalDPS",              sortable: true,  align: "end"   as const },
            { title: "有效 DPS",     key: "effectiveDPS",          sortable: true,  align: "end"   as const },
            { title: "個人有效 DPS", key: "individualEffectiveDPS",sortable: true,  align: "end"   as const },
        ];

        // ────────────────────────────────────────────────────────────
        // Mode B：單場 × 多人
        // ────────────────────────────────────────────────────────────
        const selectedFightKey = ref<string>("");
        const selectedJob      = ref<string>("");
        // Quick Boss 按鈕所選的 race filter（null = 不限）
        const modeBRaceFilter  = ref<number | null>(null);

        // 場次清單（若 modeBRaceFilter 有值則只顯示該 Boss 的場次）
        const fightOptions = computed(() => {
            const map = actorManager.value.entityMap;
            const opts: { key: string; label: string; lastAt: number }[] = [];
            for (const [key, e] of Object.entries(map)) {
                const ea = e as EntityActor;
                if (ea.isPC || ea.ownerId || !ea.takeDamages.length) continue;
                if (modeBRaceFilter.value !== null && ea.raceId !== modeBRaceFilter.value) continue;
                const name     = raceNameMap.value[ea.raceId] ?? `Race ${ea.raceId}`;
                const firstAt  = ea.takeDamages.reduce((m, d) => Math.min(m, d.At), Infinity);
                const lastAt   = ea.takeDamages.reduce((m, d) => Math.max(m, d.At), -Infinity);
                const totalDmg = ea.takeDamages.reduce((s, d) => s + d.Damage, 0);
                opts.push({ key, lastAt, label: `${name}${name.includes(String(ea.raceId)) ? '' : `（#${ea.raceId}）`}  ${humanReadableNumber(totalDmg)}  ${fmtDateTime(firstAt)}` });
            }
            return opts.sort((a, b) => b.lastAt - a.lastAt);
        });

        // 選取場次的 summary
        const fightSummaryB = computed(() => {
            if (!selectedFightKey.value) return null;
            return buildBossSummary(selectedFightKey.value, actorManager.value, [], undefined, { ignoreSummonsExceptPuppet: ignoreSummonsExceptPuppet.value, passiveRules: passiveRules, stardustConfigs: luckConfig.value.stardustConfigs });
        });

        // 此場次有哪些職業（null 排除）
        const jobOptions = computed(() => {
            const jobs = new Set<string>();
            for (const p of fightSummaryB.value?.players ?? [])
                if (p.jobName) jobs.add(p.jobName);
            return [...jobs].sort();
        });

        // 選取職業的玩家（null 已排除）
        const comparedPlayers = computed((): PlayerSummary[] => {
            if (!fightSummaryB.value || !selectedJob.value) return [];
            return fightSummaryB.value.players.filter((p) => p.jobName === selectedJob.value);
        });

        const modeBSession = computed(() => fightSummaryB.value?.session ?? null);

        // Mode B overview items
        const modeBOverviewItems = computed(() => {
            const partyTotal = fightSummaryB.value?.totalDamage ?? 0;
            return comparedPlayers.value.map((p) => ({ ...p, totalPartyDamage: partyTotal }));
        });

        const modeBOverviewAvg = computed(() => {
            const items = modeBOverviewItems.value;
            if (items.length < 2) return null;
            const partyTotal = fightSummaryB.value?.totalDamage ?? 0;
            const mean = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / vals.length;
            return {
                totalDamage:             mean(items.map((i) => i.totalDamage)),
                partyRatio:              partyTotal > 0 ? mean(items.map((i) => i.totalDamage / partyTotal)) : 0,
                activeDamageAbs:         mean(items.map((i) => i.totalDamage * i.activeDamageRatio)),
                activeDamageRatio:       mean(items.map((i) => i.activeDamageRatio)),
                passiveDamageAbs:        mean(items.map((i) => i.totalDamage * i.passiveDamageRatio)),
                passiveDamageRatio:      mean(items.map((i) => i.passiveDamageRatio)),
                critRate:                mean(items.map((i) => i.critRate)),
                totalDPS:                mean(items.map((i) => i.totalDPS)),
                effectiveDPS:            mean(items.map((i) => i.effectiveDPS)),
                individualEffectiveDPS:  mean(items.map((i) => i.individualEffectiveDPS)),
                individualEffectiveTime: mean(items.map((i) => i.individualEffectiveTime)),
                effectiveDuration:       modeBSession.value?.effectiveDuration ?? 0,
            };
        });

        // Mode B 技能欄（依總傷害合計降序）
        const modeBSkillIds = computed(() => {
            const totals        = new Map<number, number>();
            const skillIsPassive = new Map<number, boolean>();
            const totalPlayerDamage = comparedPlayers.value.reduce((s, p) => s + p.totalDamage, 0);
            for (const p of comparedPlayers.value) {
                for (const s of p.skillStats) {
                    totals.set(s.skillId, (totals.get(s.skillId) ?? 0) + s.totalDamage);
                    if (!skillIsPassive.has(s.skillId))
                        skillIsPassive.set(s.skillId, s.triggerSources.length > 0);
                }
            }
            return [...totals.entries()]
                .filter(([id, dmg]) => {
                    if (filterLowRatio.value && totalPlayerDamage > 0 && dmg / totalPlayerDamage < 0.002) return false;
                    const isPassive = skillIsPassive.get(id) ?? false;
                    if (!showPassive.value && isPassive)  return false;
                    if (!showActive.value  && !isPassive) return false;
                    return true;
                })
                .sort((a, b) => b[1] - a[1])
                .map(([id]) => id);
        });

        const modeBPassiveSkillIds = computed((): Set<number> => {
            const ids = new Set<number>();
            for (const p of comparedPlayers.value)
                for (const s of p.skillStats)
                    if (s.triggerSources.length > 0) ids.add(s.skillId);
            return ids;
        });

        // Mode B quick select：按下後 filter 場次清單並 auto-select 最新場
        const quickFightRaceId = computed(() => modeBRaceFilter.value);

        const quickSelectFight = (raceId: number) => {
            if (modeBRaceFilter.value === raceId) {
                // 再次點擊 → 取消 filter
                modeBRaceFilter.value  = null;
                selectedFightKey.value = "";
                return;
            }
            modeBRaceFilter.value = raceId;
            // 自動選最新一場該 Boss 的場次
            const map = actorManager.value.entityMap;
            let best = ""; let bestTime = -Infinity;
            for (const [key, e] of Object.entries(map)) {
                const ea = e as EntityActor;
                if (ea.isPC || ea.ownerId || ea.raceId !== raceId || !ea.takeDamages.length) continue;
                const lastAt = ea.takeDamages.reduce((m, d) => Math.max(m, d.At), -Infinity);
                if (lastAt > bestTime) { bestTime = lastAt; best = key; }
            }
            if (best) selectedFightKey.value = best;
        };

        const modeBOverviewHeaders = [
            { title: "名稱",         key: "name",                   sortable: true,  align: "start" as const },
            { title: "總傷害",       key: "totalDamage",            sortable: true,  align: "end"   as const },
            { title: "隊伍佔比",     key: "partyRatio",             sortable: false, align: "end"   as const },
            { title: "主動傷害",     key: "activeDamage",           sortable: false, align: "end"   as const },
            { title: "被動傷害",     key: "passiveDamage",          sortable: false, align: "end"   as const },
            { title: "暴擊率",       key: "critRate",               sortable: true,  align: "end"   as const },
            { title: "全場 DPS",     key: "totalDPS",               sortable: true,  align: "end"   as const },
            { title: "有效 DPS",     key: "effectiveDPS",           sortable: true,  align: "end"   as const },
            { title: "個人有效 DPS", key: "individualEffectiveDPS", sortable: true,  align: "end"   as const },
        ];

        // ────────────────────────────────────────────────────────────
        // 共用：指標平均計算（Mode A / B 共用 SkillSource 介面）
        // ────────────────────────────────────────────────────────────
        const calcAvg = (sources: SkillSource[], skillId: number, metricKey: string): string | null => {
            const stats = sources.map((src) => src.skillStats.find((x) => x.skillId === skillId)).filter(Boolean) as SkillStat[];
            if (!stats.length) return null;

            if (metricKey === "ratio") {
                const vals = sources
                    .map((src) => { const s = src.skillStats.find((x) => x.skillId === skillId); return s && src.totalDamage > 0 ? s.totalDamage / src.totalDamage : null; })
                    .filter((v): v is number => v !== null);
                return vals.length ? fmtPct(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
            }
            if (metricKey === "totalDamage") {
                const vals = stats.map((s) => s.totalDamage);
                return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length).toLocaleString();
            }

            if (metricKey === "useCount") {
                const vals = stats.map((s) => s.totalHits);
                return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
            }
            if (metricKey === "usesPerMinute") {
                const vals = stats.map((s) => s.usesPerMinute);
                return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
            }
            if (metricKey === "damagePerUse") {
                const vals = stats.map((s) => s.damagePerUse);
                return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length).toLocaleString();
            }
            if (metricKey === "critRate") {
                const eligible = stats.filter((s) => !s.noCritRate);
                if (!eligible.length) return null;
                const vals = eligible.map((s) => s.critRate);
                return fmtPct(vals.reduce((a, b) => a + b, 0) / vals.length);
            }
            if (metricKey === "critDmg") {
                const maxVals = stats.map((s) => s.maxCritDamage).filter((v): v is number => v !== null);
                const minVals = stats.map((s) => s.minCritDamage).filter((v): v is number => v !== null);
                if (!maxVals.length) return null;
                const maxStr = Math.round(maxVals.reduce((a, b) => a + b, 0) / maxVals.length).toLocaleString();
                const minStr = Math.round(minVals.reduce((a, b) => a + b, 0) / minVals.length).toLocaleString();
                return `${minStr} ~ ${maxStr}`;
            }
            if (metricKey === "nonCritDmg") {
                const maxVals = stats.map((s) => s.maxNonCritDamage).filter((v): v is number => v !== null);
                const minVals = stats.map((s) => s.minNonCritDamage).filter((v): v is number => v !== null);
                if (!maxVals.length) return null;
                const maxStr = Math.round(maxVals.reduce((a, b) => a + b, 0) / maxVals.length).toLocaleString();
                const minStr = Math.round(minVals.reduce((a, b) => a + b, 0) / minVals.length).toLocaleString();
                return `${minStr} ~ ${maxStr}`;
            }
            return null;
        };

        // ── 格式化 ──────────────────────────────────────────────────
        const fmtDateTime = (ts: number) => {
            const opts: Intl.DateTimeFormatOptions = showDate.value
                ? { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }
                : { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false };
            return new Date(ts * 1000).toLocaleString("zh-TW", opts);
        };

        const fmtTime = (ts: number) => {
            if (showDate.value) {
                return new Date(ts * 1000).toLocaleString("zh-TW", {
                    month: "2-digit", day: "2-digit",
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                    hour12: false,
                });
            }
            return new Date(ts * 1000).toLocaleTimeString("zh-TW", { hour12: false });
        };

        const fmtDuration = (sec: number) => {
            if (!isFinite(sec) || sec <= 0) return "0s";
            if (sec < 60) return `${sec.toFixed(1)}s`;
            const m = Math.floor(sec / 60);
            const s = Math.round(sec % 60).toString().padStart(2, "0");
            return `${m}m ${s}s`;
        };

        const fmtPct = (ratio: number) => (!isFinite(ratio) ? "—" : `${(ratio * 100).toFixed(1)}%`);


        const fmtDps = (dps: number) => {
            if (!isFinite(dps) || dps <= 0) return "—";
            if (dps >= 1_000_000) return `${(dps / 1_000_000).toFixed(2)}M/s`;
            if (dps >= 1_000)     return `${(dps / 1_000).toFixed(1)}k/s`;
            return `${dps.toFixed(0)}/s`;
        };

        const clearSelections = () => {
            selectedPlayerName.value = "";
            selectedRaceId.value     = null;
            selectedFightKey.value   = "";
            selectedJob.value        = "";
            modeBRaceFilter.value    = null;
        };
        // 手動從下拉選單選擇場次時，若 race 與 filter 不符則清除 filter
        watch(selectedFightKey, (key) => {
            if (!key || modeBRaceFilter.value === null) return;
            const ea = actorManager.value.entityMap[key] as EntityActor | undefined;
            if (ea && ea.raceId !== modeBRaceFilter.value) modeBRaceFilter.value = null;
        });

        onMounted(()   => appEvent.value.addEventListener("clear", clearSelections));
        onUnmounted(() => appEvent.value.removeEventListener("clear", clearSelections));

        // ── 人品判斷 ──────────────────────────────────────────────
        const luckHasBarrier    = ref(false);
        const luckHasDestrSet   = ref(false);

        // Mode B：每位玩家各自的裝備狀態（結界 / 破壞套）
        const modeBPlayerToggles = ref<Record<string, { barrier: boolean; destrSet: boolean }>>({});
        function togglePlayerBarrier(name: string) {
            const cur = modeBPlayerToggles.value[name] ?? { barrier: false, destrSet: false };
            modeBPlayerToggles.value = { ...modeBPlayerToggles.value, [name]: { ...cur, barrier: !cur.barrier } };
        }
        function togglePlayerDestrSet(name: string) {
            const cur = modeBPlayerToggles.value[name] ?? { barrier: false, destrSet: false };
            modeBPlayerToggles.value = { ...modeBPlayerToggles.value, [name]: { ...cur, destrSet: !cur.destrSet } };
        }

        function zScore(actual: number, n: number, p: number): number | null {
            if (n <= 0) return null;
            const expected = n * p;
            const std = Math.sqrt(n * p * (1 - p));
            return std > 0 ? (actual - expected) / std : null;
        }
        function zLabel(z: number | null): string {
            if (z === null) return '—';
            if (z >= 2)  return '超好運';
            if (z >= 1)  return '稍好運';
            if (z > -1)  return '正常';
            if (z > -2)  return '稍衰';
            return '超衰';
        }
        function zColor(z: number | null): string {
            if (z === null) return '';
            if (z >= 2)  return 'success';
            if (z >= 1)  return 'light-green-darken-1';
            if (z > -1)  return '';
            if (z > -2)  return 'orange';
            return 'error';
        }


        type StardustSlotData = {
            label: string;
            triggerCount: number;
            avgIntervalSec: number | null;
        };

        type StardustRowData = {
            totalTriggers: number;
            totalPPM: number;
            totalAvgIntervalSec: number | null;
            theoreticalMaxPPM: number;
            triggerEfficiency: number;
            slots: StardustSlotData[];
        };

        type LuckRow = {
            label: string;
            actual: number;
            n: number;
            p: number;
            stardustData?: StardustRowData;
        };

        function makeLuckRows(luck: LuckStats, effectiveTimeSec = 0, hasBarrier = false, hasDestrSet = false): LuckRow[] {
            const critP = ((hasBarrier ? luckConfig.value.critRateBarrierBonus : 0) + luckConfig.value.critRateBase) / 100;
            const chainP = ((hasDestrSet ? luckConfig.value.chainRateSetBonus : 0) + luckConfig.value.chainRateBase) / 100;
            const rows: LuckRow[] = [
                { label: '暴擊率', actual: luck.critHits, n: luck.eligibleCritHits, p: critP },
                { label: '連續攻擊', actual: luck.chainTriggers, n: luck.eligibleChainHits, p: chainP },
            ];
            const sdCfgs = luckConfig.value.stardustConfigs.filter((c) => c.skillId !== 0);
            if (sdCfgs.length > 0) {
                const slots: StardustSlotData[] = sdCfgs.map((cfg) => {
                    const triggerCount = luck.stardustStats.find((s) => s.skillId === cfg.skillId)?.triggers ?? 0;
                    const avgIntervalSec = triggerCount > 0 && effectiveTimeSec > 0 ? effectiveTimeSec / triggerCount : null;
                    return { label: cfg.label, triggerCount, avgIntervalSec };
                });
                const totalTriggers = slots.reduce((sum, s) => sum + s.triggerCount, 0);
                const totalPPM = effectiveTimeSec > 0 ? (totalTriggers * 60) / effectiveTimeSec : 0;
                const totalAvgIntervalSec = totalTriggers > 0 && effectiveTimeSec > 0 ? effectiveTimeSec / totalTriggers : null;
                const sharedCd = sdCfgs[0].cooldownSec;
                const theoreticalMaxPPM = sharedCd > 0 ? 60 / sharedCd : 30;
                const triggerEfficiency = theoreticalMaxPPM > 0 ? totalPPM / theoreticalMaxPPM : 0;
                rows.push({ label: '星塵', actual: totalTriggers, n: 0, p: 0, stardustData: { totalTriggers, totalPPM, totalAvgIntervalSec, theoreticalMaxPPM, triggerEfficiency, slots } });
            }
            return rows;
        }

        // Mode A：每場次的人品資料（列=項目，欄=場次）
        type FightLuckEntry = { fightKey: string; startAt: number; rows: LuckRow[] };
        const luckFightDataA = computed((): FightLuckEntry[] => {
            if (!selectedPlayerName.value || !filteredFightSummaries.value.length) return [];
            return filteredFightSummaries.value.map((f) => ({
                fightKey: f.entityKey,
                startAt: f.startAt,
                rows: makeLuckRows(f.player.luckStats, f.player.individualEffectiveTime, luckHasBarrier.value, luckHasDestrSet.value),
            }));
        });

        // Mode B：每位玩家的 luckStats
        const luckRowsB = computed((): { name: string; rows: LuckRow[] }[] => {
            const summary = fightSummaryB.value;
            if (!summary) return [];
            return summary.players.map((p) => {
                const t = modeBPlayerToggles.value[p.name] ?? { barrier: false, destrSet: false };
                return { name: p.name, rows: makeLuckRows(p.luckStats, p.individualEffectiveTime, t.barrier, t.destrSet) };
            });
        });

        return {
            mode,
            // Mode A
            selectedPlayerName, selectedRaceId,
            playerOptions, raceOptions, overviewItems, overviewHeaders, overviewAvg, fightsByJob,
            selectedFightKeys, filteredFightSummaries,
            modeABossCondRows, modeAPlayerCondRows, modeACustomCondRows, modeACCAnalysis,
            filteredModeABossCondRows, modeABossCondOptions, bossCondIncludeIds, bossCondFilterOpen,
            filteredModeAPlayerCondRows, modeAPlayerCondOptions, playerCondIncludeIds, playerCondFilterOpen,
            collapsedModeABossCond, collapsedModeAPlayerCond,
            collapsedModeACustomCond, collapsedModeACCAnalysis, ccSettingsDialogOpen,
            expandedCCSkillIds, toggleExpandedCCSkill,
            // Mode B
            selectedFightKey, selectedJob,
            fightOptions, jobOptions, comparedPlayers, modeBSession,
            modeBOverviewItems, modeBOverviewHeaders, modeBOverviewAvg, modeBSkillIds, modeBPassiveSkillIds,
            quickFightRaceId, quickSelectFight,
            // 共用
            quickBossOptions, skillNameMap, raceNameMap, condNameMap, region,
            condIconUrl,
            SKILL_METRICS, calcAvg,
            filterLowRatio, showActive, showPassive, showCountOnly, activeSkillMetrics, activeSkillMetricsPassive, getDisplayName, renamePlayers,
            collapsedModeAOverview, collapsedSkillDetail, toggleSkillDetailCollapsed,
            collapsedModeBOverview, collapsedModeBSkill,
            fmtDateTime, fmtTime, fmtDuration, fmtPct, fmtDps,
            SKILL_METRICS_PASSIVE, ccPctColor, OTHER_TRIGGER_SOURCE_ID,
            sc,
            ignoreSummonsExceptPuppet,
            luckConfig, luckHasBarrier, luckHasDestrSet,
            modeBPlayerToggles, togglePlayerBarrier, togglePlayerDestrSet,
            luckFightDataA, luckRowsB,
            zScore, zLabel, zColor,
        };
    },
});
</script>

<style scoped>
/* ── 人品表格 ────────────────────────────────────────────────── */
.luck-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
}
.luck-table th,
.luck-table td {
    padding: 4px 10px;
    text-align: right;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    white-space: nowrap;
}
.luck-table th:first-child,
.luck-table td:first-child {
    text-align: left;
}
.luck-table thead th {
    color: rgba(255,255,255,0.5);
    font-weight: 500;
}

/* ── HUD root ────────────────────────────────────────────────── */
.hud-wrap {
    min-height: 100%;
    background: #0a0d14;
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
    color: #dde1e7;
}

/* v-card */
.hud-wrap :deep(.v-card) {
    background: #10151e !important;
    border-color: rgba(255,255,255,0.08) !important;
    color: #dde1e7 !important;
}
.hud-wrap :deep(.v-card-title) {
    color: rgba(221,225,231,0.85) !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    letter-spacing: 0.03em;
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}
.hud-wrap :deep(.v-divider) {
    border-color: rgba(255,255,255,0.07) !important;
}

/* v-data-table */
.hud-wrap :deep(.v-data-table) {
    background: transparent !important;
    color: #dde1e7 !important;
    font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
}
.hud-wrap :deep(.v-data-table__th) {
    color: rgba(221,225,231,0.38) !important;
    font-size: 9px !important;
    font-weight: 700 !important;
    letter-spacing: 0.10em !important;
    text-transform: uppercase;
    background: transparent !important;
}
.hud-wrap :deep(.v-data-table__td) {
    color: rgba(221,225,231,0.8) !important;
    border-color: rgba(255,255,255,0.04) !important;
    font-size: 12px;
}
.hud-wrap :deep(.v-data-table__tr:hover > .v-data-table__td) {
    background: rgba(255,255,255,0.025) !important;
}
.hud-wrap :deep(.v-data-table__tr--even > .v-data-table__td) {
    background: rgba(255,255,255,0.012) !important;
}
.hud-wrap :deep(.v-data-table-footer) {
    color: rgba(221,225,231,0.45) !important;
}

/* v-btn-toggle */
.hud-wrap :deep(.v-btn-toggle .v-btn) {
    color: rgba(221,225,231,0.45) !important;
    font-size: 12px;
}
.hud-wrap :deep(.v-btn-toggle .v-btn--active) {
    color: #00d4c8 !important;
    background: rgba(0,212,200,0.12) !important;
}

/* v-btn quick select */
.hud-wrap :deep(.v-btn[color="primary"]) {
    background: rgba(0,212,200,0.18) !important;
    color: #00d4c8 !important;
    border-color: rgba(0,212,200,0.4) !important;
}

/* v-autocomplete / v-select */
.hud-wrap :deep(.v-field) {
    background: rgba(255,255,255,0.04) !important;
    color: rgba(221,225,231,0.85) !important;
    font-size: 13px;
}
.hud-wrap :deep(.v-field__outline) { --v-field-border-opacity: 0.18; }
.hud-wrap :deep(.v-label) {
    color: rgba(221,225,231,0.45) !important;
    font-size: 12px;
}
.hud-wrap :deep(.v-select__selection-text) { color: rgba(221,225,231,0.85); }
.hud-wrap :deep(.v-field__append-inner .v-icon) { color: rgba(221,225,231,0.35) !important; }

/* v-alert */
.hud-wrap :deep(.v-alert) {
    background: rgba(255,255,255,0.03) !important;
    color: rgba(221,225,231,0.6) !important;
}

/* v-checkbox */
.hud-wrap :deep(.v-checkbox .v-label) {
    color: rgba(221,225,231,0.7) !important;
    font-size: 12px;
}

/* ── Overview average row ────────────────────────────────────── */
.overview-avg-row td {
    background: rgba(0,212,200,0.06) !important;
    border-top: 2px solid rgba(0,212,200,0.22) !important;
    font-weight: 600;
    font-size: 12px;
    color: rgba(221,225,231,0.9) !important;
    padding-top: 5px !important;
    padding-bottom: 5px !important;
}
.avg-label {
    color: #00d4c8;
    font-weight: 700;
    font-size: 12px;
}

/* ── Skill comparison table ──────────────────────────────────── */
.skill-cmp-table {
    border-collapse: collapse;
    font-size: 0.75rem;
    white-space: nowrap;
    color: #dde1e7;
}
.skill-cmp-table th,
.skill-cmp-table td {
    border: 1px solid rgba(255,255,255,0.06);
    padding: 4px 10px;
}
.skill-cmp-table thead th {
    background: rgba(255,255,255,0.04);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(221,225,231,0.38);
    text-align: center;
}
.cell-sticky {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #10151e;
}
.cell-skill-name {
    min-width: 100px;
    font-weight: 600;
    color: rgba(221,225,231,0.9);
    text-align: left !important;
    vertical-align: middle;
}
.cell-metric-label {
    min-width: 80px;
    color: rgba(221,225,231,0.4);
    font-weight: normal;
    text-align: left;
    background: #10151e;
}
.cell-fight-col { min-width: 130px; font-size: 0.7rem; color: rgba(221,225,231,0.75); }
.cell-val       { text-align: right; min-width: 100px; color: rgba(221,225,231,0.8); }
.cell-avg {
    background: rgba(0,212,200,0.06) !important;
    font-weight: 600;
    color: #00d4c8 !important;
    border-left: 2px solid rgba(0,212,200,0.2) !important;
}
.row-skill-sep td {
    border-top: 2px solid rgba(255,255,255,0.10) !important;
}
.skill-cmp-table tbody tr:hover td {
    background: rgba(255,255,255,0.025);
}
.skill-filter-bar {
    background: rgba(255,255,255,0.02);
    gap: 0;
}

/* 觸發來源：三欄 grid 對齊 */
.cmp-trig-grid {
    display: grid;
    grid-template-columns: minmax(20px, max-content) max-content max-content;
    align-items: center;
    gap: 2px 6px;
    justify-items: end;
    width: fit-content;
    margin-left: auto;
}
.cmp-trig-icon  { image-rendering: pixelated; border-radius: 2px; cursor: help; justify-self: center; }
.cmp-trig-other { font-size: 11px; line-height: 20px; white-space: nowrap; opacity: 0.7; cursor: help; }
.cmp-trig-count { font-size: 11px; font-variant-numeric: tabular-nums; text-align: right; }
.cmp-trig-pct   { font-size: 11px; font-variant-numeric: tabular-nums; text-align: right; }

/* 技能欄：icon + 名稱並排 */
.cmp-skill-cell {
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
}
.cmp-skill-icon {
    flex-shrink: 0;
    image-rendering: pixelated;
    border-radius: 2px;
}

/* ── Condition / CC coverage comparison table ────────────────── */
.cov-cmp-table {
    border-collapse: collapse;
    font-size: 0.75rem;
    white-space: nowrap;
    color: #dde1e7;
    width: 100%;
}
.cov-cmp-table th,
.cov-cmp-table td {
    border: 1px solid rgba(255,255,255,0.06);
    padding: 4px 10px;
}
.cov-cmp-table thead th {
    background: rgba(255,255,255,0.04);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(221,225,231,0.38);
    text-align: center;
}
.cov-col-name   { min-width: 120px; text-align: left !important; font-weight: 600; color: rgba(221,225,231,0.9); }
.cov-col-metric { min-width: 70px;  text-align: left !important; color: rgba(221,225,231,0.4); font-weight: normal; background: #10151e; }
.cov-col-fight  { min-width: 110px; text-align: center; color: rgba(221,225,231,0.75); }
.cov-col-avg {
    background: rgba(0,212,200,0.06) !important;
    font-weight: 600;
    color: #00d4c8 !important;
    border-left: 2px solid rgba(0,212,200,0.2) !important;
}
.cov-name-cell {
    font-weight: 500;
    color: rgba(221,225,231,0.9);
    vertical-align: middle;
    white-space: nowrap;
}
/* 有 icon 時的內部 flex 容器（不能直接在 td 上用 flex，會破壞 rowspan） */
.cov-name-inner {
    display: flex;
    align-items: center;
    gap: 6px;
}
.cov-metric-label {
    color: rgba(221,225,231,0.4);
    font-size: 11px;
    background: #10151e;
    white-space: nowrap;
}
.cov-val-cell {
    text-align: center;
    color: rgba(221,225,231,0.8);
}
.cov-eff  { font-weight: 600; }
.cov-tot  { font-size: 10px; color: rgba(221,225,231,0.4); margin-top: 1px; }
.cov-cond-icon {
    image-rendering: pixelated;
    border-radius: 2px;
    vertical-align: middle;
    flex-shrink: 0;
}
.cov-cmp-table tbody tr:hover td {
    background: rgba(255,255,255,0.025);
}
.cc-skill-row { cursor: pointer; }
.cc-skill-row:hover td { background: rgba(255,255,255,0.045) !important; }
.cc-fight-subrow td {
    background: rgba(0,0,0,0.18);
    font-size: 0.72rem;
}
.cc-fight-subrow:hover td { background: rgba(255,255,255,0.02) !important; }
.cc-subrow-date {
    padding-left: 28px !important;
    color: rgba(221,225,231,0.4) !important;
    font-size: 0.7rem !important;
}
.cc-overall-row td {
    border-top: 2px solid rgba(255,255,255,0.12) !important;
    background: rgba(255,255,255,0.03);
}
.cov-cmp-table .row-skill-sep td {
    border-top: 2px solid rgba(255,255,255,0.10) !important;
}

/* ── 截圖 ────────────────────────────────────────────────────── */
.screenshot-selected {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 1px;
}
.screenshot-bar {
    position: sticky;
    bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(16, 21, 30, 0.92);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 212, 200, 0.3);
    border-radius: 8px;
    padding: 8px 12px;
    z-index: 10;
    margin-top: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.5);
}
.screenshot-bar-count {
    font-size: 12px;
    color: rgba(221, 225, 231, 0.7);
    margin-right: 4px;
}
</style>
